import {
  useCallback, useEffect, useMemo, useRef, useState,
  type CSSProperties, type MouseEvent, type PointerEvent as ReactPointerEvent,
} from 'react';
import { Link } from 'react-router-dom';
import {
  PLANS,
  STRATEGY_CATALOG,
  TERMIX_TIMEFRAMES,
  VOLUME_TAB_UNLOCKS,
  bookWatchSnapshot,
  deepHistoryTarget,
  friendlyMarketError,
  isWeexSymbolError,
  liveCandleLimit,
  maxHeldCandles,
  rangeTimeEndingSoon,
  sanitizeMarketSymbol,
  timeframeSeconds,
  type Candle,
  type StrategyKind,
  type TermixTimeframe,
  type TradeRange,
} from '@termix/shared';
import { api, ApiError, clearToken } from '../lib/api';
import ChartPane, { type DrawTool } from '../components/ChartPane';
import StrategyPanel from '../components/StrategyPanel';
import IndicatorsPanel from '../components/IndicatorsPanel';
import TerminalPanel from '../components/TerminalPanel';
import { emptyPayload, type ChartPayload } from '../lib/drawings';
import { mergeCandleSeries } from '../lib/indicators';
import { sketchBacktestMarkers, type BtMarker } from '../lib/backtest';
import { fmtPrice } from '../lib/priceFormat';
import { chipCopy, chipTabTag, useI18n } from '../lib/i18n';
import { useTheme } from '../lib/theme';
import { useMediaQuery } from '../lib/useMediaQuery';
import { attachRealizedPnl, buildFillLevelDashes } from '../lib/fillLevels';
import { loadEquityHistory, pushEquitySample, type EquityMarket, type EquitySample } from '../lib/equityHistory';
import MonitorSparklines from '../components/MonitorSparklines';
import PairPicker from '../components/PairPicker';
import MarginControls, { type ExchangeMarginConfig, type MarginMode } from '../components/MarginControls';
import ProtectiveOrders, {
  defaultProtective,
  resolveProtectivePrices,
  type ProtectiveState,
} from '../components/ProtectiveOrders';
import PositionTools, {
  normalizeOpenPosition,
  positionKey,
  type OpenPosition,
} from '../components/PositionTools';

type MarketKind = 'futures' | 'spot';

const ASIDE_W_KEY = 'termix_aside_w';
const BASEMENT_H_KEY = 'termix_basement_h';
const ASIDE_W_MIN = 260;
const ASIDE_W_MAX = 560;
const ASIDE_W_DEFAULT = 340;
const BASEMENT_H_MIN = 140;
const BASEMENT_H_MAX = 520;
const BASEMENT_H_DEFAULT = 240;

const clampPx = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const readStoredPx = (key: string, fallback: number, lo: number, hi: number) => {
  try {
    const n = Number(localStorage.getItem(key));
    if (Number.isFinite(n)) return clampPx(n, lo, hi);
  } catch { /* ignore */ }
  return fallback;
};

type Workspace = {
  id: string;
  title: string;
  symbol: string;
  timeframe: string;
  market_type?: MarketKind | null;
  strategy_kind?: string | null;
  strategy_params?: Record<string, unknown> | null;
  trade_range?: TradeRange | null;
  api_key_id?: string | null;
};

/** One undo/redo step: drawings + pair + TF + range (10 back / 10 forward). */
type DeskSnap = {
  symbol: string;
  timeframe: string;
  marketType: MarketKind;
  tradeRange: TradeRange | null;
  payload: ChartPayload;
};

const MAX_UNDO = 10;

type Plan = {
  id: string;
  name: string;
  tabLimit: number;
  priceUsdtMonthly: number;
  tradedVolumeUsdt?: number;
  baseTabLimit?: number;
  volumeUnlockedTabs?: number;
};

const nextVolumeUnlock = (tradedVolumeUsdt: number) => {
  const vol = Number(tradedVolumeUsdt) || 0;
  for (const tier of VOLUME_TAB_UNLOCKS) {
    if (vol < tier.minVolumeUsdt) return { nextAt: tier.minVolumeUsdt, nextTabs: tier.tabLimit };
  }
  return { nextAt: null as number | null, nextTabs: null as number | null };
};
type ApiKey = { id: string; alias: string; exchange: string; status: string };
type RightTab = 'trade' | 'strategy' | 'keys';

const POPULAR_SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT', 'DOGEUSDT', 'BNBUSDT', 'ADAUSDT', 'AVAXUSDT'];

/** Trade-range chip: readable prices (not float noise) + short time window. */
const fmtRange = (r?: TradeRange | null, locale = 'en-US') => {
  if (!r) return '';
  const fmtPx = (n: unknown) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return '—';
    const abs = Math.abs(x);
    const d = abs >= 1000 ? 2 : abs >= 100 ? 3 : abs >= 1 ? 4 : abs >= 0.01 ? 5 : 6;
    return x.toLocaleString(locale, { maximumFractionDigits: d, minimumFractionDigits: 0 });
  };
  const px = `${fmtPx(r.priceLow)}–${fmtPx(r.priceHigh)}`;
  if (r.timeFrom != null && r.timeTo != null) {
    const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' };
    const a = new Date(r.timeFrom * 1000).toLocaleString(locale, opts);
    const b = new Date(r.timeTo * 1000).toLocaleString(locale, opts);
    return `${px} · ${a}→${b}`;
  }
  return px;
};

const TG_HANDLE_KEY = 'termix_tg_handle';
const TG_BOT = String((import.meta as any).env?.VITE_TERMIX_TG_BOT || 'TermixDeskBot').replace(/^@/, '');

export default function DeskPage() {
  const { lang, setLang, t } = useI18n();
  const { theme, setTheme } = useTheme();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeId, setActiveId] = useState('');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [candles, setCandles] = useState<Candle[]>([]);
  const [status, setStatus] = useState<'idle' | 'live' | 'offline'>('idle');
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [orderType, setOrderType] = useState<'MARKET' | 'LIMIT' | 'STOP_MARKET' | 'STOP'>('MARKET');
  const [timeInForce, setTimeInForce] = useState<'GTC' | 'IOC' | 'FOK'>('GTC');
  const [qty, setQty] = useState('0.01');
  /** How the Size field is interpreted before sending coin qty to WEEX. */
  const [sizeMode, setSizeMode] = useState<'coins' | 'usdt' | 'pct'>('coins');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [protective, setProtective] = useState<ProtectiveState>(defaultProtective);
  const [posProtective, setPosProtective] = useState<ProtectiveState>(defaultProtective);
  const [selectedPosKey, setSelectedPosKey] = useState<string | null>(null);
  const [closePct, setClosePct] = useState(100);
  const [posBusy, setPosBusy] = useState(false);
  const [strategyKind, setStrategyKind] = useState<StrategyKind>('volume_churn');
  const [strategyParams, setStrategyParams] = useState<Record<string, unknown>>({ ...STRATEGY_CATALOG.volume_churn.defaults });
  const [onlyProfitPct, setOnlyProfitPct] = useState('0.15');
  const [onlyProfitEnabled, setOnlyProfitEnabled] = useState(true);
  const [msg, setMsg] = useState('');
  const [customSymbol, setCustomSymbol] = useState('');
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [selectedKeyId, setSelectedKeyId] = useState('');
  const [keyForm, setKeyForm] = useState({ alias: 'main', apiKey: '', apiSecret: '', passphrase: '' });
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [keyBusy, setKeyBusy] = useState(false);
  const [account, setAccount] = useState<{
    balance?: {
      equity: number;
      available: number;
      availableMargin?: number;
      usedMargin?: number;
      maintenanceMargin?: number;
      unrealizedPnl?: number;
      marginRatio?: number;
    };
    positions?: any[];
  } | null>(null);
  const [candlesKey, setCandlesKey] = useState('');
  const [orders, setOrders] = useState<any[]>([]);
  const [depth, setDepth] = useState<{ bids: Array<[number, number]>; asks: Array<[number, number]> } | null>(null);
  const [trades, setTrades] = useState<any[]>([]);
  const [orderBusy, setOrderBusy] = useState(false);
  const [tool, setTool] = useState<DrawTool>('pointer');
  const [payload, setPayload] = useState<ChartPayload>(emptyPayload());
  // Persist chrome tabs so a theme toggle / remount never kicks you back to Order + Positions.
  const [rightTab, setRightTab] = useState<RightTab>(() => {
    try {
      const v = sessionStorage.getItem('termix_right_tab');
      if (v === 'trade' || v === 'strategy' || v === 'keys') return v;
    } catch { /* ignore */ }
    return 'trade';
  });
  // Default away from Стакан so depth is not fetched until user opens it
  const [termTab, setTermTab] = useState<'depth' | 'positions' | 'orders' | 'trades' | 'balance'>(() => {
    try {
      const v = sessionStorage.getItem('termix_term_tab');
      if (v === 'depth' || v === 'positions' || v === 'orders' || v === 'trades' || v === 'balance') return v;
    } catch { /* ignore */ }
    return 'positions';
  });
  /** Left column: split · chart covers basement · basement covers chart. */
  const [termDock, setTermDock] = useState<'split' | 'chart' | 'terminal'>(() => {
    try {
      const v = sessionStorage.getItem('termix_term_dock');
      if (v === 'split' || v === 'chart' || v === 'terminal') return v;
    } catch { /* ignore */ }
    return 'split';
  });
  /** Desktop: user-scaled panes (px). Abs sizes — Mac window chrome must not reflow vh/fr jumps. */
  const [asideWidth, setAsideWidth] = useState(() => (
    readStoredPx(ASIDE_W_KEY, ASIDE_W_DEFAULT, ASIDE_W_MIN, ASIDE_W_MAX)
  ));
  const [basementH, setBasementH] = useState(() => (
    readStoredPx(BASEMENT_H_KEY, BASEMENT_H_DEFAULT, BASEMENT_H_MIN, BASEMENT_H_MAX)
  ));
  const [paneDrag, setPaneDrag] = useState<null | 'aside' | 'basement'>(null);
  const [drawDirty, setDrawDirty] = useState(false);
  const [marketSymbols, setMarketSymbols] = useState<string[]>(POPULAR_SYMBOLS);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [drawColor, setDrawColor] = useState(() => (
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'ivory'
      ? '#2a241c'
      : '#e8edf5'
  ));
  const [indOpen, setIndOpen] = useState(false);
  const indFlyoutRef = useRef<HTMLDivElement>(null);
  const [colorOpen, setColorOpen] = useState(false);
  const [rangeUseTime, setRangeUseTime] = useState(true);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [liveRuns, setLiveRuns] = useState<Array<{
    id: string;
    status: string;
    workspace_id?: string | null;
    symbol?: string;
    kind?: string;
    api_key_id?: string | null;
    params?: Record<string, unknown> | null;
    created_at?: string | null;
  }>>([]);
  const [lastStopMode, setLastStopMode] = useState<'leave' | 'close' | 'soft' | null>(null);
  const [posGroupByTab, setPosGroupByTab] = useState(false);
  const [runNotice, setRunNotice] = useState<string | null>(null);
  const [sizeWarn, setSizeWarn] = useState<{ text: string; level: 'warn' | 'info' } | null>(null);
  const [me, setMe] = useState<{ id: string; email: string; planId: string; isAdmin?: boolean } | null>(null);
  const [paywall, setPaywall] = useState<{
    quote: { amountUsdt: number; platformFeeUsdt: number; plan?: { name?: string }; treasuryAddress?: string | null };
    wallet: { address: string; balanceUsdt: number };
    suggestedPlanId: string;
  } | null>(null);
  const [pwdForm, setPwdForm] = useState({ current: '', next: '' });
  const [tgHandle, setTgHandle] = useState(() => {
    try { return localStorage.getItem(TG_HANDLE_KEY) || ''; } catch { return ''; }
  });
  const [depositWallet, setDepositWallet] = useState<{ address: string; balanceUsdt: number } | null>(null);
  const [riskPct, setRiskPct] = useState('2');
  const [riskMaxPct, setRiskMaxPct] = useState('10');
  const [deskCross, setDeskCross] = useState<{ x: number; y: number } | null>(null);
  const [chartFocus, setChartFocus] = useState(false);
  const [marginMode, setMarginMode] = useState<MarginMode>('cross');
  const [leverage, setLeverage] = useState('10');
  const [exchangeMargin, setExchangeMargin] = useState<ExchangeMarginConfig>(null);
  const [marginBusy, setMarginBusy] = useState(false);
  const [closedTrades, setClosedTrades] = useState<any[]>([]);
  const [liveMarkers, setLiveMarkers] = useState<BtMarker[]>([]);
  const [selectedDrawId, setSelectedDrawId] = useState<string | null>(null);
  const [histIdx, setHistIdx] = useState(-1);
  const [langOpen, setLangOpen] = useState(false);
  const [netQuality, setNetQuality] = useState<'stable' | 'connecting' | 'weak' | 'offline'>('connecting');
  /** Separate from market WS — key present ≠ WEEX account readable. */
  const [accountHealth, setAccountHealth] = useState<'idle' | 'ok' | 'empty' | 'error'>('idle');
  const [cabinetExpanded, setCabinetExpanded] = useState(false);
  const [monitorBusy, setMonitorBusy] = useState(false);
  const [stopDialog, setStopDialog] = useState(false);
  const [stopBusy, setStopBusy] = useState(false);
  const [equitySamples, setEquitySamples] = useState<EquitySample[]>([]);
  /** Which wallet the Cabinet sparkline follows (Spot ≠ Futures — separate series). */
  const [monitorMarket, setMonitorMarket] = useState<EquityMarket>('futures');
  const monitorMarketRef = useRef<EquityMarket>('futures');
  monitorMarketRef.current = monitorMarket;
  /** Other wallet snapshot so Cabinet can show Futures + Spot side by side. */
  const [siblingBalance, setSiblingBalance] = useState<{ market: EquityMarket; equity: number; available: number } | null>(null);
  /** Tick so tab live-dot blink updates as range timeTo approaches. */
  const [nowTick, setNowTick] = useState(() => Math.floor(Date.now() / 1000));
  const deskRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const cabinetCardRef = useRef<HTMLDivElement>(null);
  const candlesKeyRef = useRef('');
  /** Cap for merge: live limit until user deep-loads history for this series. */
  const heldMaxRef = useRef(1000);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyExhausted, setHistoryExhausted] = useState(false);
  const restReadyKeyRef = useRef('');
  const savedRangeTimeRef = useRef<{ timeFrom: number; timeTo: number } | null>(null);
  const histRef = useRef<DeskSnap[]>([]);
  /** Skip drawings reload wipe while undo/redo or changeSymbol owns the payload. */
  const applyingHistRef = useRef(false);
  /** Seed undo stack only when the workspace tab changes. */
  const histTabRef = useRef<string>('');
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  const loadGenRef = useRef(0);
  const stratPersistTimer = useRef<number | undefined>(undefined);

  const active = useMemo(() => workspaces.find((w) => w.id === activeId) || workspaces[0], [workspaces, activeId]);

  const anyTimedRange = useMemo(
    () => workspaces.some((w) => w.trade_range?.timeTo != null),
    [workspaces],
  );
  useEffect(() => {
    if (!anyTimedRange) return;
    const id = window.setInterval(() => setNowTick(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [anyTimedRange]);

  // Key is shared across tabs: this tab → any tab with a key → first saved key
  const activeKeyId = selectedKeyId
    || active?.api_key_id
    || workspaces.find((w) => w.api_key_id)?.api_key_id
    || keys[0]?.id
    || '';
  const activeMarket: MarketKind = active?.market_type === 'spot' ? 'spot' : 'futures';
  const marketQs = activeMarket === 'spot' ? 'market=spot' : 'market=futures';
  // Keep in lockstep with REST/WS candlesKey (`market:SYMBOL:tf`) or ChartPane skips paint.
  const seriesKey = `${activeMarket}:${sanitizeMarketSymbol(active?.symbol) || 'BTCUSDT'}:${active?.timeframe || '15m'}`;
  const liveByWorkspace = useMemo(() => {
    const map = new Map<string, (typeof liveRuns)[0]>();
    for (const r of liveRuns) {
      if (!r.workspace_id) continue;
      if (!['armed', 'signals_only', 'in_position'].includes(r.status)) continue;
      map.set(r.workspace_id, r);
    }
    return map;
  }, [liveRuns]);
  /** Symbol → short chip tag for positions table (empty when no live chip). */
  const chipBySymbol = useMemo(() => {
    const map: Record<string, string> = {};
    const norm = (s: unknown) => String(s || '')
      .toUpperCase()
      .replace(/^CMT_?/i, '')
      .replace(/[:/\-_]/g, '')
      .replace(/USDTUSDT$/, 'USDT');
    for (const r of liveRuns) {
      if (!['armed', 'signals_only', 'in_position'].includes(r.status)) continue;
      const sym = norm(r.symbol);
      if (!sym || !r.kind) continue;
      if (!map[sym]) map[sym] = chipTabTag(t, r.kind);
    }
    return map;
  }, [liveRuns, t]);
  const activeLiveRun = active?.id ? liveByWorkspace.get(active.id) : undefined;
  const activeRunStatus = activeLiveRun?.status || (activeRunId ? 'armed' : null);
  const drawingToolsOn = tool !== 'pointer';
  const uPnL = useMemo(() => {
    const list = account?.positions || [];
    return list.reduce((s, p) => s + (Number(p.unrealizedPnl ?? p.upnl ?? p.uPnL) || 0), 0);
  }, [account?.positions]);

  useEffect(() => {
    if (!drawingToolsOn) {
      setDeskCross(null);
      return;
    }
    const onMove = (e: PointerEvent) => {
      const el = deskRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDeskCross({ x: e.clientX - r.left, y: e.clientY - r.top });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [drawingToolsOn]);

  useEffect(() => {
    if (!langOpen) return;
    const onDoc = (e: Event) => {
      const node = e.target as Node | null;
      if (node && langMenuRef.current?.contains(node)) return;
      setLangOpen(false);
    };
    // next tick so the opening click does not immediately close
    const id = window.setTimeout(() => {
      document.addEventListener('mousedown', onDoc);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [langOpen]);

  useEffect(() => {
    let cancelled = false;
    // Drop the previous market's 1k+ list immediately — otherwise Spot↔Futures pair
    // table shows foreign symbols with “—” 24h%/Vol until the new catalog arrives.
    setMarketSymbols(POPULAR_SYMBOLS);
    void (async () => {
      try {
        const data = await api<{ symbols: string[]; product?: string }>(`/api/market/symbols?${marketQs}`);
        if (cancelled) return;
        if (Array.isArray(data.symbols) && data.symbols.length) {
          setMarketSymbols(data.symbols);
        }
      } catch {
        /* keep POPULAR_SYMBOLS fallback */
      }
    })();
    return () => { cancelled = true; };
  }, [marketQs]);

  const refreshRuns = useCallback(async () => {
    try {
      const data = await api<{ runs: Array<{
        id: string;
        status: string;
        workspace_id?: string | null;
        symbol?: string;
        kind?: string;
        api_key_id?: string | null;
        params?: Record<string, unknown> | null;
        created_at?: string | null;
      }> }>('/api/strategies');
      const all = data.runs || [];
      // Keep recently stopped delist notices visible on the tab
      const live = all.filter((r) => ['armed', 'signals_only', 'in_position'].includes(r.status));
      setLiveRuns(live);
      const noticeRun = all.find((r) =>
        (r.workspace_id === activeId || (active && r.symbol === active.symbol))
        && r.params?.delistNotice,
      );
      setRunNotice(noticeRun?.params?.delistNotice ? String(noticeRun.params.delistNotice) : null);
      const forTab = live.find((r) => r.workspace_id && r.workspace_id === activeId)
        || live.find((r) => active && r.symbol === active.symbol);
      setActiveRunId(forTab?.id || null);
      const sameTab = (r: (typeof all)[0]) =>
        (r.workspace_id && r.workspace_id === activeId)
        || (active && String(r.symbol || '').toUpperCase() === String(active.symbol || '').toUpperCase());
      if (forTab?.params?.stopAfterExit) {
        setLastStopMode('soft');
      } else if (!forTab) {
        const stopped = all.find((r) =>
          sameTab(r)
          && ['stopped', 'killed'].includes(r.status)
          && (r.params?.lastStopMode === 'close' || r.params?.lastStopMode === 'leave' || r.params?.lastStopMode === 'soft'),
        );
        const mode = stopped?.params?.lastStopMode;
        setLastStopMode(mode === 'close' || mode === 'leave' || mode === 'soft' ? mode : null);
      } else {
        setLastStopMode(null);
      }
    } catch {
      /* ignore */
    }
  }, [activeId, active?.symbol]);

  const refreshWorkspaces = async () => {
    const data = await api<{ workspaces: Workspace[]; plan: Plan }>('/api/workspaces');
    setWorkspaces(data.workspaces);
    setPlan(data.plan);
    if (!activeId && data.workspaces[0]) setActiveId(data.workspaces[0].id);
    else if (activeId && !data.workspaces.some((w) => w.id === activeId) && data.workspaces[0]) {
      setActiveId(data.workspaces[0].id);
    }
  };

  const refreshKeys = async () => {
    const data = await api<{ keys: ApiKey[] }>('/api/keys');
    setKeys(data.keys);
    if (!selectedKeyId && data.keys[0]) setSelectedKeyId(data.keys[0].id);
  };

  const refreshAccount = async (keyId: string, symbol?: string, opts?: { quiet?: boolean }) => {
    if (!keyId) {
      setAccount(null);
      setOrders([]);
      setAccountHealth('idle');
      setSiblingBalance(null);
      if (!opts?.quiet) setMsg(t.needKeyCabinet);
      return;
    }
    try {
      const [pos, ord] = await Promise.all([
        api<{
          positions: any[];
          balance: {
            equity: number;
            available: number;
            availableMargin?: number;
            usedMargin?: number;
            maintenanceMargin?: number;
            unrealizedPnl?: number;
            marginRatio?: number;
          };
        }>(`/api/keys/${keyId}/positions?market=${activeMarket}${symbol ? `&symbol=${encodeURIComponent(symbol)}` : ''}`),
        api<{ orders: any[] }>(`/api/keys/${keyId}/orders?market=${activeMarket}${symbol ? `&symbol=${encodeURIComponent(symbol)}` : ''}`).catch(() => ({ orders: [] as any[] })),
      ]);
      setAccount(pos);
      setOrders(ord.orders || []);
      const eq = Number(pos.balance?.equity ?? 0);
      const av = Number(pos.balance?.available ?? 0);
      const upnlSum = (pos.positions || []).reduce(
        (s: number, p: any) => s + (Number(p.unrealizedPnl ?? p.upnl ?? p.uPnL) || 0),
        0,
      );
      const balUpnl = Number(pos.balance?.unrealizedPnl);
      const upnl = Number.isFinite(balUpnl) ? balUpnl : upnlSum;
      const healthy = Number.isFinite(eq) && (Math.abs(eq) > 1e-8 || Math.abs(av) > 1e-8);
      setAccountHealth(healthy ? 'ok' : 'empty');
      // Spot and Futures wallets are separate — keep separate equity sparkline histories.
      const market: EquityMarket = activeMarket === 'spot' ? 'spot' : 'futures';
      if (Number.isFinite(eq)) {
        const next = pushEquitySample(keyId, eq, upnl, {
          market,
          minGapMs: opts?.quiet ? 10_000 : 0,
        });
        // Only poke the visible sparkline when it matches this wallet.
        if (monitorMarketRef.current === market) setEquitySamples(next);
      }
      // Soft-fetch the other wallet so Cabinet can show both (Elite is never in Termix).
      const other: EquityMarket = market === 'spot' ? 'futures' : 'spot';
      void api<{
        balance: { equity?: number; available?: number; availableMargin?: number };
      }>(`/api/keys/${keyId}/positions?market=${other}`).then((sib) => {
        const seq = Number(sib.balance?.equity ?? 0);
        const sav = Number(sib.balance?.availableMargin ?? sib.balance?.available ?? 0);
        setSiblingBalance({
          market: other,
          equity: Number.isFinite(seq) ? seq : 0,
          available: Number.isFinite(sav) ? sav : 0,
        });
        if (Number.isFinite(seq)) {
          const sibNext = pushEquitySample(keyId, seq, 0, { market: other, minGapMs: 10_000 });
          if (monitorMarketRef.current === other) setEquitySamples(sibNext);
        }
      }).catch(() => { /* ignore sibling */ });
      if (!opts?.quiet) {
        setMsg(t.accountRefreshed(eq, av));
      }
    } catch (e) {
      setAccount(null);
      setOrders([]);
      setAccountHealth('error');
      if (opts?.quiet) return;
      if (e instanceof ApiError) {
        setMsg(e.body?.error || `${t.accountRefreshFail}: ${e.message}`);
      } else {
        setMsg(`${t.accountRefreshFail}: ${(e as Error).message}`);
      }
    }
  };

  const refreshMargin = useCallback(async (keyId: string, symbol?: string) => {
    if (!keyId || !symbol) { setExchangeMargin(null); return; }
    try {
      const data = await api<{ config: ExchangeMarginConfig }>(
        `/api/keys/${keyId}/symbol-config?symbol=${encodeURIComponent(symbol)}`,
      );
      const cfg = data.config;
      setExchangeMargin(cfg);
      if (cfg) {
        const mt = String(cfg.marginType || '').toUpperCase();
        if (mt === 'ISOLATED') setMarginMode('isolated');
        else if (mt === 'CROSSED') setMarginMode('cross');
        const lev = mt === 'ISOLATED'
          ? (cfg.isolatedLongLeverage || cfg.isolatedShortLeverage)
          : cfg.crossLeverage;
        if (lev) setLeverage(String(lev));
      }
    } catch {
      setExchangeMargin(null);
    }
  }, []);

  const drawingsReqRef = useRef(0);
  const parseDrawingPayload = (p: ChartPayload | null | undefined): ChartPayload => {
    if (p && typeof p === 'object') {
      return {
        drawings: Array.isArray(p.drawings) ? p.drawings : [],
        indicators: { ...emptyPayload().indicators, ...(p.indicators || {}) },
      };
    }
    return emptyPayload();
  };

  const makeDeskSnap = (overrides?: Partial<DeskSnap>): DeskSnap => ({
    symbol: String(active?.symbol || 'BTCUSDT'),
    timeframe: String(active?.timeframe || '15m'),
    marketType: activeMarket,
    tradeRange: active?.trade_range ? structuredClone(active.trade_range) : null,
    payload: structuredClone(payloadRef.current),
    ...overrides,
    ...(overrides?.payload ? { payload: structuredClone(overrides.payload) } : {}),
    ...(overrides?.tradeRange !== undefined
      ? { tradeRange: overrides.tradeRange ? structuredClone(overrides.tradeRange) : null }
      : {}),
  });

  const loadDrawings = async (symbol: string, opts?: { seedHistory?: boolean }) => {
    const reqId = ++drawingsReqRef.current;
    const seed = Boolean(opts?.seedHistory);
    try {
      const data = await api<{ drawing: { payload?: ChartPayload } | null }>(`/api/drawings/${symbol}`);
      if (reqId !== drawingsReqRef.current) return; // stale response after fast pair switch
      const next = parseDrawingPayload(data.drawing?.payload);
      setPayload(next);
      if (seed) {
        histRef.current = [makeDeskSnap({
          symbol,
          timeframe: String(active?.timeframe || '15m'),
          tradeRange: active?.trade_range ? structuredClone(active.trade_range) : null,
          payload: next,
        })];
        setHistIdx(0);
      }
      setSelectedDrawId(null);
      setDrawDirty(false);
    } catch {
      if (reqId !== drawingsReqRef.current) return;
      const next = emptyPayload();
      setPayload(next);
      if (seed) {
        histRef.current = [makeDeskSnap({
          symbol,
          timeframe: String(active?.timeframe || '15m'),
          tradeRange: null,
          payload: next,
        })];
        setHistIdx(0);
      }
    }
  };

  const saveDrawings = async () => {
    if (!active) return;
    await api(`/api/drawings/${active.symbol}`, { method: 'PUT', body: JSON.stringify({ payload }) });
    await patchActive({ strategyKind, strategyParams });
    setDrawDirty(false);
    setMsg(t.chartSaved(active.symbol));
  };

  useEffect(() => {
    void Promise.all([
      refreshWorkspaces(),
      refreshKeys(),
      refreshRuns(),
      api<{ user: { id: string; email: string; planId: string; isAdmin?: boolean } }>('/api/auth/me')
        .then((d) => setMe(d.user))
        .catch(() => null),
      api<{ wallet: { address: string; balanceUsdt: number } }>('/api/billing/me')
        .then((d) => setDepositWallet(d.wallet))
        .catch(() => null),
    ]).catch((e) => setMsg(e.message));
  }, []);

  // Poll live strategy status for tab badges + Strategies banner
  useEffect(() => {
    const id = window.setInterval(() => { void refreshRuns(); }, 4000);
    return () => window.clearInterval(id);
  }, [refreshRuns]);

  useEffect(() => {
    void refreshRuns();
  }, [activeId, refreshRuns]);

  useEffect(() => {
    if (!active) return;
    setCustomSymbol(active.symbol);
    if (active.api_key_id) setSelectedKeyId(active.api_key_id);
    // Undo/redo and changeSymbol own the payload — don't wipe the stack.
    if (applyingHistRef.current) return;
    const tabChanged = histTabRef.current !== active.id;
    histTabRef.current = active.id;
    // Reset drawings before fetch so BTC lines don't flash on LINK
    setPayload(emptyPayload());
    setDrawDirty(false);
    void loadDrawings(active.symbol, { seedHistory: tabChanged });
  }, [active?.id, active?.symbol, active?.api_key_id]);

  // Hydrate strategy once per tab id (reload / tab switch). Local edits persist via patchActive.
  const hydratedStratTab = useRef('');
  useEffect(() => {
    if (!active) return;
    if (hydratedStratTab.current === active.id) return;
    hydratedStratTab.current = active.id;
    if (active.strategy_kind && active.strategy_kind in STRATEGY_CATALOG) {
      const k = active.strategy_kind as StrategyKind;
      setStrategyKind(k);
      setStrategyParams({ ...STRATEGY_CATALOG[k].defaults, ...(active.strategy_params || {}) });
    } else {
      setStrategyKind('volume_churn');
      setStrategyParams({ ...STRATEGY_CATALOG.volume_churn.defaults });
    }
  }, [active?.id, active?.strategy_kind, active?.strategy_params]);

  useEffect(() => {
    setMonitorMarket(activeMarket === 'spot' ? 'spot' : 'futures');
  }, [activeMarket]);

  useEffect(() => {
    setEquitySamples(activeKeyId ? loadEquityHistory(activeKeyId, monitorMarket) : []);
  }, [activeKeyId, monitorMarket]);

  useEffect(() => {
    setSiblingBalance(null);
    void refreshAccount(activeKeyId, active?.symbol, { quiet: true });
    void refreshMargin(activeKeyId, active?.symbol);
  }, [activeKeyId, active?.symbol, activeMarket, refreshMargin]);

  // Light monitoring poll (Termix desk only — not BTDD SaaS charts)
  useEffect(() => {
    if (!activeKeyId) return;
    const id = window.setInterval(() => {
      void refreshAccount(activeKeyId, active?.symbol, { quiet: true });
    }, 12_000);
    return () => window.clearInterval(id);
  }, [activeKeyId, active?.symbol, activeMarket]);

  // Risk% vs min lot preview (warn when deposit clip is below exchange min)
  useEffect(() => {
    const lotSymbol = sanitizeMarketSymbol(active?.symbol);
    if (!lotSymbol) {
      setSizeWarn(null);
      return;
    }
    let cancelled = false;
    const equity = Number(account?.balance?.equity || account?.balance?.available || 0);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const q = new URLSearchParams({
            symbol: lotSymbol,
            equity: String(equity || 0),
            riskPct: String(Number(riskPct) || 2),
            market: activeMarket,
          });
          const data = await api<{
            warnMinLot?: boolean;
            size?: { qty: number; notional: number; reason: string; minLotNotional?: number; riskNotional?: number };
            lot?: { minQty: number; lotStep: number };
            price?: number;
          }>(`/api/market/lot?${q}`);
          if (cancelled) return;
          if (data.warnMinLot && data.size && data.lot) {
            setSizeWarn({
              level: 'warn',
              text: t.sizeWarnMinLot(
                data.lot.minQty,
                data.size.notional,
                Number(data.size.riskNotional || 0),
                lotSymbol,
              ),
            });
          } else if (data.size) {
            setSizeWarn({
              level: 'info',
              text: t.sizePreview(data.size.qty, data.size.notional, data.lot?.lotStep || 0),
            });
          } else {
            setSizeWarn(null);
          }
        } catch {
          if (!cancelled) setSizeWarn(null);
        }
      })();
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [active?.symbol, riskPct, account?.balance?.equity, account?.balance?.available, t]);

  const lastSymbolRef = useRef('');
  const remappedShadowRef = useRef<Set<string>>(new Set());
  const shadowSibling = (sym: string): string | null => {
    const m = sym.toUpperCase().match(/^([A-Z0-9]+?)S(USDT)$/);
    if (!m) return null;
    const fixed = `${m[1]}${m[2]}`;
    return fixed !== sym.toUpperCase() ? fixed : null;
  };

  // Leave WEEX shadow pairs (FILSUSDT → FILUSDT). Optimistic local update so UI
  // does not stick on a dead symbol or spin patchActive in a loop.
  useEffect(() => {
    if (!active) return;
    const fixed = shadowSibling(active.symbol);
    if (!fixed) return;
    const gate = `${active.id}:${active.symbol}`;
    if (remappedShadowRef.current.has(gate)) return;
    remappedShadowRef.current.add(gate);
    const title = `${fixed} ${active.timeframe}`;
    setMsg(`Пара ${active.symbol} → ${fixed} (график)`);
    setWorkspaces((list) => list.map((w) => (w.id === active.id ? { ...w, symbol: fixed, title } : w)));
    void api(`/api/workspaces/${active.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ symbol: fixed, title }),
    }).catch(() => {
      remappedShadowRef.current.delete(gate);
    });
  }, [active?.id, active?.symbol, active?.timeframe]);

  // Market WS + REST candles (forming bar keeps painting via poll fan-out)
  useEffect(() => {
    if (!active) return;
    let alive = true;
    const rawSym = shadowSibling(active.symbol) || active.symbol;
    const wantSymbol = sanitizeMarketSymbol(rawSym);
    const wantTf = active.timeframe;
    const wantMarket: MarketKind = active.market_type === 'spot' ? 'spot' : 'futures';
    if (!wantSymbol) {
      setStatus('offline');
      setNetQuality('offline');
      setCandles([]);
      setMsg(t.badPair(String(active.symbol || rawSym || '')));
      return;
    }
    // Must match ChartPane seriesKey (`market:symbol:tf`) or candles never paint → «No data».
    const key = `${wantMarket}:${wantSymbol}:${wantTf}`;
    const gen = ++loadGenRef.current;
    const liveCap = liveCandleLimit(wantTf);
    // Do NOT clear candles to [] on TF switch — empty wipe + Failed to fetch left a dead chart.
    // seriesKey change makes ChartPane ignore stale bars until the matching history arrives.
    if (candlesKeyRef.current && candlesKeyRef.current !== key) {
      setStatus('idle');
      setNetQuality('connecting');
      restReadyKeyRef.current = '';
      heldMaxRef.current = liveCap;
      setHistoryBusy(false);
      setHistoryExhausted(false);
      setMsg(t.loadingTf);
    } else if (!candlesKeyRef.current) {
      heldMaxRef.current = liveCap;
      setHistoryExhausted(false);
    }
    lastSymbolRef.current = wantSymbol;
    const candlesUrl = `/api/market/candles?symbol=${encodeURIComponent(wantSymbol)}&timeframe=${encodeURIComponent(wantTf)}&market=${wantMarket}`;
    const symbolStillActive = () => {
      const cur = sanitizeMarketSymbol(shadowSibling(active.symbol) || active.symbol);
      const curMarket: MarketKind = active.market_type === 'spot' ? 'spot' : 'futures';
      return cur === wantSymbol && active.timeframe === wantTf && curMarket === wantMarket && gen === loadGenRef.current;
    };
    const load = async (attempt = 0) => {
      try {
        const data = await api<{ candles: Candle[] }>(candlesUrl);
        if (!(alive && symbolStillActive())) return;
        const list = Array.isArray(data.candles) ? data.candles : [];
        if (list.length < 20) {
          setMsg(t.fewCandles(wantSymbol, wantTf));
          setNetQuality('weak');
          if (attempt < 6) {
            window.setTimeout(() => { if (alive) void load(attempt + 1); }, 1000 * (attempt + 1));
          }
          return;
        }
        const firstPaint = candlesKeyRef.current !== key;
        candlesKeyRef.current = key;
        restReadyKeyRef.current = key;
        setCandlesKey(key);
        setCandles((prev) => (firstPaint ? list : mergeCandleSeries(prev, list, heldMaxRef.current)));
        setNetQuality('stable');
        setMsg((prev) => (/klines|1142|invalid symbol|недоступна|Too few|Мало свечей|Loading|Загрузка|Failed to fetch|Market weak|Рынок слаб|retry|повтор|market error|Parameter ['"]symbol['"]/i.test(prev) ? '' : prev));
      } catch (e) {
        const msgText = (e as Error).message || '';
        if (!(alive && symbolStillActive())) return;
        // Keep painting if WS already delivered a full history for this key.
        if (restReadyKeyRef.current === key && candlesKeyRef.current === key) {
          if (attempt < 8) window.setTimeout(() => { if (alive) void load(attempt + 1); }, 1200 * (attempt + 1));
          return;
        }
        // Permanent WEEX symbol rejection — do not spin «Market weak — retrying…».
        if (isWeexSymbolError(msgText)) {
          setNetQuality('offline');
          setMsg(t.badPair(wantSymbol));
          return;
        }
        setNetQuality(attempt >= 2 ? 'weak' : 'connecting');
        if (attempt < 8 && /502|Bad Gateway|Failed to fetch|Network|TypeError/i.test(msgText)) {
          setMsg(t.netWeak);
          window.setTimeout(() => { if (alive) void load(attempt + 1); }, 900 * (attempt + 1));
          return;
        }
        setNetQuality('offline');
        setMsg(friendlyMarketError(msgText, t.netOffline));
      }
    };
    void load();
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    let ws: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let attempt = 0;
    const seriesReady = () => restReadyKeyRef.current === key && candlesKeyRef.current === key;
    const clearNetNoise = () => {
      setMsg((prev) => (/klines|1142|invalid symbol|недоступна|Too few|Мало свечей|Loading|Загрузка|Failed to fetch|Market weak|Рынок слаб|retry|повтор|market error|Parameter ['"]symbol['"]/i.test(prev) ? '' : prev));
    };
    const applyCandles = (list: Candle[]) => {
      if (list.length < 20) return false;
      const firstPaint = candlesKeyRef.current !== key;
      candlesKeyRef.current = key;
      restReadyKeyRef.current = key;
      setCandlesKey(key);
      setCandles((prev) => (firstPaint ? list : mergeCandleSeries(prev, list, heldMaxRef.current)));
      setStatus('live');
      setNetQuality('stable');
      clearNetNoise();
      return true;
    };
    // REST watchdog: keep badge honest when WS flaps (mobile / CF) but candles still refresh.
    const watchdog = window.setInterval(() => {
      if (!alive || !symbolStillActive()) return;
      void (async () => {
        try {
          const data = await api<{ candles: Candle[] }>(candlesUrl);
          if (!(alive && symbolStillActive())) return;
          const list = Array.isArray(data.candles) ? data.candles : [];
          if (list.length >= 20) applyCandles(list);
        } catch (e) {
          if (!(alive && symbolStillActive())) return;
          if (isWeexSymbolError((e as Error).message) && !seriesReady()) {
            setNetQuality('offline');
            setMsg(t.badPair(wantSymbol));
          }
        }
      })();
    }, 12000);
    const connect = () => {
      if (!alive) return;
      ws = new WebSocket(`${proto}://${location.host}/ws/market`);
      ws.onopen = () => {
        attempt = 0;
        setStatus('live');
        if (seriesReady()) {
          setNetQuality('stable');
          clearNetNoise();
        } else {
          setNetQuality((q) => (q === 'offline' || q === 'weak' ? 'connecting' : q));
        }
        ws?.send(JSON.stringify({ type: 'subscribe', symbol: wantSymbol, timeframe: wantTf, market: wantMarket }));
      };
      ws.onmessage = (ev) => {
        try {
          const payloadMsg = JSON.parse(ev.data);
          if (payloadMsg.type === 'hello') return;
          const msgSym = payloadMsg.chartSymbol || payloadMsg.symbol;
          if (payloadMsg.type === 'candles' && payloadMsg.timeframe === wantTf
            && (msgSym === wantSymbol || payloadMsg.symbol === wantSymbol || payloadMsg.symbol === active.symbol)) {
            const list = Array.isArray(payloadMsg.candles) ? payloadMsg.candles : [];
            // Full history may arrive on WS before REST — accept it so badge does not stick on «weak».
            if (list.length >= 20) {
              applyCandles(list);
              return;
            }
            // Short forming-bar bursts only update after we already have history for this key.
            if (restReadyKeyRef.current !== key) return;
          }
          if (payloadMsg.type === 'error' && alive) {
            // Transient WEEX blip: keep «stable» if chart already has this series.
            if (seriesReady()) return;
            const errText = String(payloadMsg.message || 'market error');
            if (isWeexSymbolError(errText)) {
              setNetQuality('offline');
              setMsg(t.badPair(wantSymbol));
              return;
            }
            setNetQuality('weak');
            setMsg(friendlyMarketError(errText, t.netWeak));
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (!alive) return;
        setStatus('offline');
        attempt += 1;
        // Painted chart + silent reconnect: do not flash Weak/Offline on every socket blip.
        // Only degrade after several failed reconnects without a fresh series.
        if (seriesReady()) {
          if (attempt >= 4) setNetQuality('weak');
        } else {
          setNetQuality(attempt >= 2 ? 'offline' : 'connecting');
        }
        const delay = Math.min(8000, 600 * (2 ** Math.min(attempt, 4)));
        reconnectTimer = window.setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // Browsers fire error right before close; never flip Weak over a painted series.
        if (alive && !seriesReady()) setNetQuality((q) => (q === 'stable' ? q : 'weak'));
      };
    };
    connect();
    return () => {
      alive = false;
      window.clearInterval(watchdog);
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, [active?.id, active?.symbol, active?.timeframe, active?.market_type, lang, t]);

  // Depth / trades: only on demand (user opens Стакан or Сделки) — no background spam
  const loadDepth = useCallback(async () => {
    if (!active) return;
    try {
      const d = await api<{ bids: Array<[number, number]>; asks: Array<[number, number]> }>(
        `/api/market/depth?symbol=${active.symbol}&limit=200&${marketQs}`,
      );
      setDepth({ bids: d.bids || [], asks: d.asks || [] });
    } catch (e) {
      setMsg((e as Error).message);
    }
  }, [active?.symbol]);

  const loadTrades = useCallback(async () => {
    if (!active) return;
    try {
      const t = await api<{ trades: any[] }>(`/api/market/trades?symbol=${active.symbol}&${marketQs}`);
      setTrades(t.trades || []);
    } catch (e) {
      setMsg((e as Error).message);
    }
  }, [active?.symbol]);

  /** Real WEEX fills for this pair — chart + Trades tab; survive range clear. */
  const loadLiveFills = useCallback(async () => {
    if (!active?.symbol) {
      setLiveMarkers([]);
      setClosedTrades([]);
      return;
    }
    try {
      const data = await api<{
        fills: Array<Record<string, unknown>>;
        markers: Array<{ time: number; price: number; side: string; label: string; real?: boolean }>;
      }>(`/api/fills?symbol=${encodeURIComponent(active.symbol)}&limit=200`);
      setLiveMarkers(
        (data.markers || []).map((m) => ({
          time: Number(m.time) || 0,
          price: Number(m.price) || 0,
          side: (m.side === 'short' || m.side === 'exit' || m.side === 'long' ? m.side : 'long') as BtMarker['side'],
          label: String(m.label || 'REAL'),
          real: true,
        })),
      );
      const mapped = (data.fills || []).map((f) => ({
        symbol: String(f.symbol || ''),
        side: String(f.side || ''),
        positionSide: f.position_side != null ? String(f.position_side) : null,
        price: Number(f.price) || 0,
        qty: Number(f.qty) || 0,
        quantity: Number(f.qty) || 0,
        time: f.created_at ? new Date(String(f.created_at)).getTime() : undefined,
        real: true as const,
        label: f.label != null ? String(f.label) : undefined,
        reduceOnly: Boolean(f.reduce_only),
        source: f.source != null ? String(f.source) : undefined,
      }));
      setClosedTrades(attachRealizedPnl(mapped));
    } catch {
      // Table may not exist until migrate — keep desk usable
      setLiveMarkers([]);
      setClosedTrades([]);
    }
  }, [active?.symbol]);

  useEffect(() => {
    setDepth(null);
    setTrades([]);
    void loadLiveFills();
  }, [active?.symbol, loadLiveFills]);

  useEffect(() => {
    if (termTab === 'depth') {
      void loadDepth();
      void loadTrades();
      const id = window.setInterval(() => {
        void loadDepth();
        void loadTrades();
      }, 4_000);
      return () => window.clearInterval(id);
    }
    if (termTab === 'trades') void loadLiveFills();
    return undefined;
  }, [termTab, active?.symbol, loadDepth, loadTrades, loadLiveFills]);

  // Order-block width uses L2 walls — keep a book poll while OB hints are on.
  useEffect(() => {
    if ((!payload.indicators?.orderBlocks && !payload.indicators?.obBreakers) || !active?.symbol) return;
    void loadDepth();
    const id = window.setInterval(() => { void loadDepth(); }, 6_000);
    return () => window.clearInterval(id);
  }, [payload.indicators?.orderBlocks, payload.indicators?.obBreakers, active?.symbol, loadDepth]);

  // Keep right / basement chrome across theme toggles (and accidental remounts).
  useEffect(() => {
    try { sessionStorage.setItem('termix_right_tab', rightTab); } catch { /* ignore */ }
  }, [rightTab]);

  useEffect(() => {
    if (!indOpen) return;
    const onDoc = (e: Event) => {
      const node = e.target as Node | null;
      if (node && indFlyoutRef.current?.contains(node)) return;
      setIndOpen(false);
    };
    const id = window.setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [indOpen]);
  useEffect(() => {
    try { sessionStorage.setItem('termix_term_tab', termTab); } catch { /* ignore */ }
  }, [termTab]);
  useEffect(() => {
    try { sessionStorage.setItem('termix_term_dock', termDock); } catch { /* ignore */ }
  }, [termDock]);
  useEffect(() => {
    try { localStorage.setItem(ASIDE_W_KEY, String(asideWidth)); } catch { /* ignore */ }
  }, [asideWidth]);
  useEffect(() => {
    try { localStorage.setItem(BASEMENT_H_KEY, String(basementH)); } catch { /* ignore */ }
  }, [basementH]);

  const beginAsideResize = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = asideWidth;
    setPaneDrag('aside');
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: PointerEvent) => {
      // Handle sits on the left edge of aside — drag left → wider aside.
      setAsideWidth(clampPx(startW + (startX - ev.clientX), ASIDE_W_MIN, ASIDE_W_MAX));
    };
    const onUp = () => {
      setPaneDrag(null);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [asideWidth]);

  const beginBasementResize = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startH = basementH;
    setPaneDrag('basement');
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: PointerEvent) => {
      // Handle above basement — drag up → taller basement / shorter chart.
      setBasementH(clampPx(startH - (ev.clientY - startY), BASEMENT_H_MIN, BASEMENT_H_MAX));
    };
    const onUp = () => {
      setPaneDrag(null);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [basementH]);

  // Ivory pen default — light stroke vanishes on cream chart.
  useEffect(() => {
    setDrawColor((prev) => {
      const ivory = theme === 'ivory';
      if (ivory && (prev === '#e8edf5' || prev === '#f7f3ea')) return '#2a241c';
      if (!ivory && prev === '#2a241c') return '#e8edf5';
      return prev;
    });
  }, [theme]);

  // REAL fills refresh (independent of range / backtest sketch)
  useEffect(() => {
    if (!active?.symbol) return;
    const id = window.setInterval(() => { void loadLiveFills(); }, 20_000);
    return () => window.clearInterval(id);
  }, [active?.symbol, loadLiveFills]);

  const openTabPaywall = async () => {
    // Always open modal (never only a side toast). Wallet may be empty until billing migrate.
    const fallbackQuote = {
      amountUsdt: 10,
      platformFeeUsdt: 0.1,
      plan: { name: 'Pro' },
      treasuryAddress: null as string | null,
    };
    try {
      const data = await api<{
        quote: any;
        wallet: { address: string; balanceUsdt: number };
        billingError?: string | null;
      }>('/api/billing/upgrade-intent', {
        method: 'POST',
        body: JSON.stringify({ planId: 'pro' }),
      });
      const wallet = data.wallet?.address
        ? data.wallet
        : { address: '', balanceUsdt: 0 };
      setPaywall({
        quote: data.quote || fallbackQuote,
        wallet,
        suggestedPlanId: 'pro',
      });
      if (wallet.address) setDepositWallet(wallet);
    } catch {
      setPaywall({
        quote: fallbackQuote,
        wallet: { address: '', balanceUsdt: 0 },
        suggestedPlanId: 'pro',
      });
    }
  };

  const addTab = async () => {
    const limit = Number(plan?.tabLimit || 2);
    if (workspaces.length >= limit) {
      await openTabPaywall();
      return;
    }
    try {
      const data = await api<{ workspace: Workspace }>('/api/workspaces', {
        method: 'POST',
        body: JSON.stringify({ symbol: active?.symbol || 'BTCUSDT', timeframe: active?.timeframe || '15m' }),
      });
      await refreshWorkspaces();
      setActiveId(data.workspace.id);
      setMsg(t.tabAdded);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 402 || e.body?.code === 'TAB_LIMIT')) {
        const up = e.body?.upgrade;
        if (up?.quote && up?.wallet) {
          setPaywall({
            quote: up.quote,
            wallet: up.wallet,
            suggestedPlanId: up.suggestedPlanId || 'pro',
          });
          setDepositWallet(up.wallet);
          return;
        }
        await openTabPaywall();
        return;
      }
      setMsg((e as Error).message);
    }
  };

  const openUpgrade = async (planId: string) => {
    const fallbackQuote = {
      amountUsdt: planId === 'desk' ? 20 : 10,
      platformFeeUsdt: planId === 'desk' ? 0.2 : 0.1,
      plan: { name: planId === 'desk' ? 'Desk' : 'Pro' },
      treasuryAddress: null as string | null,
    };
    try {
      const data = await api<{ quote: any; wallet: { address: string; balanceUsdt: number } }>('/api/billing/upgrade-intent', {
        method: 'POST',
        body: JSON.stringify({ planId }),
      });
      const wallet = data.wallet?.address ? data.wallet : { address: '', balanceUsdt: 0 };
      setPaywall({ quote: data.quote || fallbackQuote, wallet, suggestedPlanId: planId });
      if (wallet.address) setDepositWallet(wallet);
      setRightTab('keys');
    } catch {
      setPaywall({
        quote: fallbackQuote,
        wallet: { address: '', balanceUsdt: 0 },
        suggestedPlanId: planId,
      });
      setRightTab('keys');
    }
  };

  const closeTab = async (id: string, e?: MouseEvent) => {
    e?.stopPropagation();
    if (workspaces.length <= 1) { setMsg(t.lastTab); return; }
    try {
      await api(`/api/workspaces/${id}`, { method: 'DELETE' });
      const next = workspaces.filter((w) => w.id !== id);
      if (activeId === id) setActiveId(next[0]?.id || '');
      await refreshWorkspaces();
      setMsg(t.tabClosed);
    } catch (err) { setMsg((err as Error).message); }
  };

  const patchActive = async (patch: Record<string, unknown>) => {
    if (!active) return;
    try {
      const data = await api<{ workspace: Workspace }>(`/api/workspaces/${active.id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      if (data?.workspace) {
        setWorkspaces((list) => list.map((w) => (w.id === data.workspace.id ? { ...w, ...data.workspace } : w)));
      } else {
        await refreshWorkspaces();
      }
    } catch (e) {
      setMsg((e as Error).message);
      await refreshWorkspaces();
    }
  };

  /** Switch to an existing tab for this symbol, or retarget the active tab. */
  const jumpToSymbolTab = async (raw: string) => {
    const symbol = raw.trim().toUpperCase().replace(/^CMT_?/i, '').replace(/[^A-Z0-9]/g, '');
    if (!symbol || symbol.length < 6) return;
    const existing = workspaces.find((w) => String(w.symbol || '').toUpperCase() === symbol);
    if (existing) {
      setActiveId(existing.id);
      setTermTab('positions');
      return;
    }
    await changeSymbol(symbol);
    setTermTab('positions');
  };

  const pushDeskHistory = useCallback((snap: DeskSnap) => {
    setHistIdx((idx) => {
      const base = histRef.current.slice(0, Math.max(0, idx + 1));
      base.push({
        symbol: snap.symbol,
        timeframe: snap.timeframe,
        marketType: snap.marketType,
        tradeRange: snap.tradeRange ? structuredClone(snap.tradeRange) : null,
        payload: structuredClone(snap.payload),
      });
      // 10 back from tip (length ≤ MAX_UNDO+1). Redo side is whatever remains after undo.
      while (base.length > MAX_UNDO + 1) base.shift();
      histRef.current = base;
      return base.length - 1;
    });
  }, []);

  const applyDeskSnap = useCallback(async (snap: DeskSnap) => {
    if (!active) return;
    applyingHistRef.current = true;
    setSelectedDrawId(null);
    setPayload(structuredClone(snap.payload));
    setCustomSymbol(snap.symbol);
    setDrawDirty(true);
    const tf = snap.timeframe || active.timeframe || '15m';
    const title = `${snap.symbol} ${tf}`;
    setWorkspaces((list) => list.map((w) => (
      w.id === active.id
        ? {
            ...w,
            symbol: snap.symbol,
            timeframe: tf,
            title,
            trade_range: snap.tradeRange,
          }
        : w
    )));
    try {
      await api(`/api/workspaces/${active.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          symbol: snap.symbol,
          timeframe: tf,
          title,
          tradeRange: snap.tradeRange,
        }),
      });
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      // Let the symbol/tab effect see the flag, then release.
      window.setTimeout(() => { applyingHistRef.current = false; }, 0);
    }
  }, [active]);

  const changeMarketType = async (next: MarketKind) => {
    if (!active || activeMarket === next) return;
    applyingHistRef.current = true;
    setCandles([]);
    await patchActive({
      marketType: next,
      title: `${active.symbol} ${active.timeframe}`,
      tradeRange: null,
    });
    if (next === 'spot') {
      setStrategyParam('shortEnabled', false);
    }
    pushDeskHistory(makeDeskSnap({ marketType: next, tradeRange: null }));
    applyingHistRef.current = false;
    setMsg(next === 'spot'
      ? (lang === 'ru' ? `Спот · ${active.symbol}` : `Spot · ${active.symbol}`)
      : (lang === 'ru' ? `Фьючерсы · ${active.symbol}` : `Futures · ${active.symbol}`));
  };

  const changeSymbol = async (raw: string) => {
    const symbol = sanitizeMarketSymbol(raw);
    if (!symbol) { setMsg(t.pairExample); return; }
    if (symbol === active?.symbol) return;
    const tf = String(active?.timeframe || '15m');
    // Keep current desk state as undo tip (drawings + old pair + range).
    if (!histRef.current.length) {
      pushDeskHistory(makeDeskSnap());
    }
    applyingHistRef.current = true;
    setCustomSymbol(symbol);
    setCandles([]);
    setPayload(emptyPayload());
    setDrawDirty(false);
    // Drop workspace trade box — prices from previous pair (e.g. BTC 80k) poison LINK ~11 scale.
    await patchActive({
      symbol,
      title: `${symbol} ${tf}`,
      tradeRange: null,
    });
    // Load drawings for the new pair without seeding/wiping undo stack.
    let nextPayload = emptyPayload();
    try {
      const data = await api<{ drawing: { payload?: ChartPayload } | null }>(`/api/drawings/${symbol}`);
      nextPayload = parseDrawingPayload(data.drawing?.payload);
    } catch { /* empty */ }
    setPayload(nextPayload);
    pushDeskHistory(makeDeskSnap({
      symbol,
      timeframe: tf,
      tradeRange: null,
      payload: nextPayload,
    }));
    applyingHistRef.current = false;
    // Stop bots for this workspace — they still hold the old symbol until re-armed
    try {
      const runs = await api<{ runs: Array<{ id: string; status: string; workspace_id?: string }> }>('/api/strategies');
      const activeStatuses = new Set(['armed', 'signals_only', 'in_position']);
      const wsId = active?.id;
      await Promise.all(
        (runs.runs || [])
          .filter((r) => activeStatuses.has(r.status) && wsId && r.workspace_id === wsId)
          .map((r) => api(`/api/strategies/${r.id}/stop`, { method: 'POST' }).catch(() => null)),
      );
      await refreshRuns();
    } catch { /* ignore */ }
    setMsg(t.pairReset(symbol));
  };

  const onRangePick = useCallback(async (box: TradeRange) => {
    const normalized: TradeRange = rangeUseTime
      ? box
      : { priceLow: box.priceLow, priceHigh: box.priceHigh, timeFrom: null, timeTo: null };
    // Optimistic: keep the box where the user left it while PATCH runs (drag used to feel sticky).
    if (active?.id) {
      setWorkspaces((list) => list.map((w) => (
        w.id === active.id ? { ...w, trade_range: normalized } : w
      )));
    }
    await patchActive({ tradeRange: normalized });
    pushDeskHistory(makeDeskSnap({ tradeRange: normalized }));
    const loc = lang === 'ru' ? 'ru-RU' : 'en-US';
    if (activeRunId) {
      setMsg(t.rangeApplied(fmtRange(normalized, loc)));
    } else {
      setMsg(t.rangeTab(fmtRange(normalized, loc)));
    }
    // Only leave the range-draw tool; do not yank cursor mode after every edge tweak.
    setTool((cur) => (cur === 'range' ? 'pointer' : cur));
  }, [active?.id, active?.symbol, active?.timeframe, activeRunId, rangeUseTime, lang, t, pushDeskHistory]);

  const onPayloadChange = useCallback((next: ChartPayload, opts?: { history?: boolean }) => {
    setPayload(next);
    setDrawDirty(true);
    if (opts?.history === false) return;
    pushDeskHistory(makeDeskSnap({ payload: next }));
  }, [pushDeskHistory]);

  const loadMoreHistory = useCallback(async () => {
    if (!active?.symbol || historyBusy || historyExhausted) return;
    const tf = String(active.timeframe || '15m');
    const target = deepHistoryTarget(tf);
    if (target == null) {
      setHistoryExhausted(true);
      return;
    }
    const sym = sanitizeMarketSymbol(shadowSibling(active.symbol) || active.symbol);
    if (!sym) return;
    const before = candles[0]?.time;
    if (!before) return;
    if (candles.length >= target) {
      setHistoryExhausted(true);
      return;
    }
    setHistoryBusy(true);
    try {
      // Page ~800 bars per left-pan; keep WEEX / chart snappy.
      const page = Math.min(800, Math.max(200, target - candles.length));
      const q = new URLSearchParams({
        symbol: sym,
        timeframe: tf,
        limit: String(page),
        before: String(before),
        market: activeMarket,
      });
      const data = await api<{ candles: Candle[] }>(`/api/market/candles/history?${q}`);
      const older = Array.isArray(data.candles) ? data.candles : [];
      heldMaxRef.current = maxHeldCandles(tf);
      setCandles((prev) => {
        const prevFirst = prev[0]?.time;
        const merged = mergeCandleSeries(older, prev, heldMaxRef.current);
        const grew = merged.length > prev.length
          && (prevFirst == null || (merged[0] && merged[0].time < prevFirst));
        if (!grew) {
          setHistoryExhausted(true);
        } else if (merged.length >= target) {
          setHistoryExhausted(true);
        }
        return merged;
      });
    } catch (e) {
      setMsg(t.indHistoryFail((e as Error).message || 'error'));
    } finally {
      setHistoryBusy(false);
    }
  }, [active?.symbol, active?.timeframe, candles, historyBusy, historyExhausted, t]);

  const undoDraw = useCallback(() => {
    const idx = histIdx;
    if (idx <= 0) return;
    const prev = histRef.current[idx - 1];
    if (!prev) return;
    setHistIdx(idx - 1);
    void applyDeskSnap(prev);
  }, [histIdx, applyDeskSnap]);

  const redoDraw = useCallback(() => {
    const idx = histIdx;
    if (idx >= histRef.current.length - 1) return;
    const next = histRef.current[idx + 1];
    if (!next) return;
    setHistIdx(idx + 1);
    void applyDeskSnap(next);
  }, [histIdx, applyDeskSnap]);

  // Ctrl+Z / Ctrl+Shift+Z (or Ctrl+Y) — same stack as the toolbar buttons.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const tag = (e.target as HTMLElement | null)?.tagName || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undoDraw();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        redoDraw();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undoDraw, redoDraw]);

  const applyMargin = useCallback(async () => {
    if (!activeKeyId || !active?.symbol) {
      setMsg(t.needKey);
      return;
    }
    setMarginBusy(true);
    try {
      const data = await api<{ config: ExchangeMarginConfig }>(`/api/keys/${activeKeyId}/margin`, {
        method: 'POST',
        body: JSON.stringify({
          symbol: active.symbol,
          marginMode,
          leverage: Number(leverage) || 10,
        }),
      });
      setExchangeMargin(data.config);
      setMsg(t.marginApplied(marginMode, leverage));
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setMarginBusy(false);
    }
  }, [activeKeyId, active?.symbol, marginMode, leverage, t]);

  const clearRange = async () => {
    await patchActive({ tradeRange: null });
    setPayload((p) => {
      const next = { ...p, drawings: p.drawings.filter((d) => d.type !== 'range') };
      pushDeskHistory(makeDeskSnap({ tradeRange: null, payload: next }));
      return next;
    });
    setDrawDirty(true);
    setSelectedDrawId(null);
    setMsg(activeRunId ? t.rangeClearedLive : t.rangeCleared);
  };

  // When opening a tab, reflect whether its box has a time window (and cache times)
  useEffect(() => {
    const r = active?.trade_range;
    if (!r) {
      savedRangeTimeRef.current = null;
      return;
    }
    if (r.timeFrom != null && r.timeTo != null) {
      savedRangeTimeRef.current = { timeFrom: r.timeFrom, timeTo: r.timeTo };
      setRangeUseTime(true);
    } else {
      setRangeUseTime(false);
    }
  }, [active?.id]);

  // Toggle «by time»: remember prior bounds when turning off; restore when turning back on
  useEffect(() => {
    if (!active?.trade_range) return;
    const r = active.trade_range;
    const hasTime = r.timeFrom != null && r.timeTo != null;
    if (!rangeUseTime && hasTime) {
      savedRangeTimeRef.current = { timeFrom: r.timeFrom!, timeTo: r.timeTo! };
      void patchActive({
        tradeRange: { priceLow: r.priceLow, priceHigh: r.priceHigh, timeFrom: null, timeTo: null },
      });
      setMsg(t.timeOff);
    } else if (rangeUseTime && !hasTime) {
      const saved = savedRangeTimeRef.current;
      if (saved) {
        void patchActive({
          tradeRange: {
            priceLow: r.priceLow,
            priceHigh: r.priceHigh,
            timeFrom: saved.timeFrom,
            timeTo: saved.timeTo,
          },
        });
        setMsg(t.timeRestored);
      }
    }
  }, [rangeUseTime]);

  const armStrategy = async () => {
    if (!active) return;
    if (!activeKeyId) {
      setRightTab('keys');
      setMsg(t.needKeyCabinet);
      return;
    }
    try {
      const params: Record<string, unknown> = {
        ...strategyParams,
        riskPct: Number(riskPct) || 2,
        riskMaxPct: Number(riskMaxPct) || 10,
        // Engine sizes from risk% + exchange lot; do not pin a tiny orderQty (GALA min=100).
        ...(strategyParams.orderQty != null ? { orderQty: strategyParams.orderQty } : {}),
        onlyProfit: { enabled: onlyProfitEnabled, minProfitPct: Number(onlyProfitPct) || 0.1 },
      };
      // Desk dials own RSI/dist/fractal — drop stale fixed knobs from older saves.
      if (strategyKind === 'hideep' || strategyKind === 'volume_churn' || strategyKind === 'redflag_dca' || strategyKind === 'order_book') {
        delete params.rsiLongBelow;
        delete params.rsiShortAbove;
        delete params.minDistPct;
        delete params.minDistPctShort;
        delete params.requireFractal;
      }
      if (activeMarket === 'spot') {
        params.shortEnabled = false;
        params.marketType = 'spot';
      } else {
        params.marketType = 'futures';
      }
      const data = await api<{ run: { id: string } }>('/api/strategies/arm', {
        method: 'POST',
        body: JSON.stringify({
          kind: strategyKind,
          symbol: active.symbol,
          timeframe: active.timeframe,
          marketType: activeMarket,
          workspaceId: active.id,
          apiKeyId: activeKeyId,
          live: true,
          tradeRange: active.trade_range || null,
          params,
        }),
      });
      setActiveRunId(data.run?.id || null);
      setLastStopMode(null);
      await patchActive({ strategyKind, strategyParams: params, apiKeyId: activeKeyId });
      await refreshRuns();
      const loc = lang === 'ru' ? 'ru-RU' : 'en-US';
      const box = active.trade_range ? ` · ${fmtRange(active.trade_range, loc)}` : '';
      setMsg(t.strategyStarted(
        chipCopy(t, strategyKind).name || strategyKind,
        riskPct,
        riskMaxPct,
        box,
      ));
    } catch (e) { setMsg((e as Error).message); }
  };

  const flattenOpenOnSymbol = async (symbol: string) => {
    if (!activeKeyId || !symbol) return;
    const list = account?.positions || [];
    const want = String(symbol).toUpperCase().replace(/_/g, '');
    for (const raw of list) {
      const n = normalizeOpenPosition(raw);
      if (!n) continue;
      if (n.symbol.replace(/_/g, '') !== want && n.symbol !== symbol) continue;
      const closeSide = n.side === 'long' ? 'sell' : 'buy';
      const positionSide = n.side === 'long' ? 'LONG' : 'SHORT';
      try {
        await api(`/api/keys/${activeKeyId}/order`, {
          method: 'POST',
          body: JSON.stringify({
            symbol: n.symbol,
            side: closeSide,
            type: 'MARKET',
            quantity: n.size,
            reduceOnly: true,
            positionSide,
          }),
        });
      } catch (e) {
        setMsg((e as Error).message);
      }
    }
    await refreshAccount(activeKeyId, symbol).catch(() => null);
    await loadLiveFills().catch(() => null);
  };

  /** mode: leave = stop bot, keep pos; close = stop + flatten; soft = no new entries until exit/range */
  const stopStrategy = async (mode: 'leave' | 'close' | 'soft' = 'leave') => {
    if (stopBusy) return;
    setStopBusy(true);
    try {
      const data = await api<{
        ok?: boolean;
        count?: number;
        needClose?: boolean;
        runs?: Array<{ id: string; status: string; symbol?: string }>;
        error?: string;
      }>('/api/strategies/stop-workspace', {
        method: 'POST',
        body: JSON.stringify({
          workspaceId: active?.id || null,
          symbol: active?.symbol || null,
          mode,
        }),
      });
      // Also stop by id if workspace call found nothing but we still have a bound run
      if (!(Number(data.count) > 0) && activeRunId) {
        await api(`/api/strategies/${activeRunId}/stop`, {
          method: 'POST',
          body: JSON.stringify({ mode }),
        }).catch(() => null);
      }
      if (mode === 'close') {
        await flattenOpenOnSymbol(active?.symbol || '');
      }
      setLastStopMode(mode);
      await refreshRuns();
      if (mode === 'soft') {
        setMsg(t.strategySoftStopped);
      } else if (mode === 'close') {
        setActiveRunId(null);
        setMsg(t.strategyStoppedClosed);
      } else {
        setActiveRunId(null);
        setMsg(t.strategyStoppedLeft);
      }
      setStopDialog(false);
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setStopBusy(false);
    }
  };

  const saveKey = async () => {
    if (keyBusy) return;
    const alias = keyForm.alias.trim() || 'main';
    if (!keyForm.apiKey.trim() || !keyForm.apiSecret.trim() || !keyForm.passphrase.trim()) {
      setMsg(t.needKeyCabinet);
      return;
    }
    setKeyBusy(true);
    try {
      const data = await api<{
        key: ApiKey;
        balance: {
          equity: number;
          available: number;
          availableMargin?: number;
          usedMargin?: number;
          maintenanceMargin?: number;
          unrealizedPnl?: number;
          marginRatio?: number;
        } | null;
        warning?: string;
        code?: string;
        egressIp?: string | null;
      }>('/api/keys', {
        method: 'POST',
        body: JSON.stringify({ ...keyForm, alias }),
      });
      await refreshKeys();
      setSelectedKeyId(data.key.id);
      if (active) await patchActive({ apiKeyId: data.key.id });
      setKeyForm({ alias: 'main', apiKey: '', apiSecret: '', passphrase: '' });
      setShowKeyForm(false);
      setRightTab('keys');
      setChartFocus(false);
      setTermTab('balance');
      if (data.balance) {
        setAccount({ balance: data.balance, positions: [] });
        setAccountHealth(
          Math.abs(data.balance.equity) > 1e-8 || Math.abs(data.balance.available) > 1e-8 ? 'ok' : 'empty',
        );
        setMsg(t.keyConnectedBal(data.key.alias, data.balance.equity, data.balance.available));
        await refreshAccount(data.key.id, active?.symbol);
        await refreshMargin(data.key.id, active?.symbol);
      } else {
        setAccount(null);
        setAccountHealth('error');
        const ip = data.egressIp || '';
        setMsg(data.warning || t.keySavedIpBlocked(data.key.alias, ip));
      }
    } catch (e) {
      if (e instanceof ApiError) {
        const ip = e.body?.egressIp ? String(e.body.egressIp) : '';
        setMsg(e.body?.error || e.message + (ip ? ` · IP ${ip}` : ''));
      } else {
        setMsg((e as Error).message);
      }
    } finally {
      setKeyBusy(false);
    }
  };

  const deleteKey = async (key: ApiKey) => {
    if (keyBusy) return;
    if (!window.confirm(t.deleteKeyConfirm(key.alias))) return;
    setKeyBusy(true);
    try {
      await api(`/api/keys/${key.id}`, { method: 'DELETE' });
      const next = await api<{ keys: ApiKey[] }>('/api/keys');
      setKeys(next.keys);
      if (selectedKeyId === key.id) setSelectedKeyId(next.keys[0]?.id || '');
      setWorkspaces((list) => list.map((w) => (w.api_key_id === key.id ? { ...w, api_key_id: null } : w)));
      if (active?.api_key_id === key.id) {
        await patchActive({ apiKeyId: next.keys[0]?.id || null }).catch(() => null);
      }
      if (!next.keys.length) {
        setAccount(null);
        setOrders([]);
        setAccountHealth('idle');
      }
      setMsg(t.keyDeleted(key.alias));
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setKeyBusy(false);
    }
  };

  const upgrade = async (planId: string) => {
    try {
      await openUpgrade(planId);
    } catch (e) { setMsg((e as Error).message); }
  };

  const tryActivatePlan = async (planId: string) => {
    try {
      await api('/api/billing/activate', { method: 'POST', body: JSON.stringify({ planId }) });
      await refreshWorkspaces();
      const me2 = await api<{ user: { id: string; email: string; planId: string; isAdmin?: boolean } }>('/api/auth/me');
      setMe(me2.user);
      setPaywall(null);
      setMsg(t.planOn(planId));
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) {
        setMsg(e.body?.message || e.message);
        if (e.body?.wallet) setDepositWallet(e.body.wallet);
        return;
      }
      setMsg((e as Error).message);
    }
  };

  const changePwd = async () => {
    try {
      await api('/api/auth/password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: pwdForm.current, nextPassword: pwdForm.next }),
      });
      setPwdForm({ current: '', next: '' });
      setMsg(t.passwordChanged);
    } catch (e) { setMsg((e as Error).message); }
  };

  const queueStrategyPersist = (kind: StrategyKind, params: Record<string, unknown>) => {
    setDrawDirty(true);
    if (stratPersistTimer.current) window.clearTimeout(stratPersistTimer.current);
    stratPersistTimer.current = window.setTimeout(() => {
      void patchActive({ strategyKind: kind, strategyParams: params });
      // Live run: merge dials into engine params (never touches openSide/pending/filledLevels).
      const runId = activeRunId;
      const live = activeRunStatus && ['armed', 'signals_only', 'in_position'].includes(activeRunStatus);
      if (runId && live) {
        const dials: Record<string, unknown> = {
          ...params,
          riskPct: Number(riskPct) || 2,
          riskMaxPct: Number(riskMaxPct) || 10,
          onlyProfit: { enabled: onlyProfitEnabled, minProfitPct: Number(onlyProfitPct) || 0.1 },
        };
        void api(`/api/strategies/${runId}/live-params`, {
          method: 'PATCH',
          body: JSON.stringify({ params: dials }),
        }).catch(() => null);
      }
    }, 350);
  };

  const setKind = (k: StrategyKind) => {
    const params = { ...STRATEGY_CATALOG[k].defaults };
    setStrategyKind(k);
    setStrategyParams(params);
    queueStrategyPersist(k, params);
    // Book lives on OB zones — flip overlay on so sketch matches live.
    if (k === 'order_book') {
      setPayload((prev) => {
        if (prev.indicators?.orderBlocks) return prev;
        return {
          ...prev,
          indicators: {
            ...prev.indicators,
            orderBlocks: true,
            orderBlocksCount: prev.indicators?.orderBlocksCount ?? 5,
            orderBlocksBook: prev.indicators?.orderBlocksBook || 'annotate',
          },
        };
      });
      setDrawDirty(true);
    }
  };

  const setStrategyParam = (key: string, value: unknown) => {
    setStrategyParams((prev) => {
      const next = { ...prev, [key]: value };
      queueStrategyPersist(strategyKind, next);
      return next;
    });
  };

  const queueRiskPersist = (nextRisk: string, nextMax: string, nextOpPct: string, nextOpOn: boolean) => {
    setDrawDirty(true);
    if (stratPersistTimer.current) window.clearTimeout(stratPersistTimer.current);
    stratPersistTimer.current = window.setTimeout(() => {
      const dials = {
        ...strategyParams,
        riskPct: Number(nextRisk) || 2,
        riskMaxPct: Number(nextMax) || 10,
        onlyProfit: { enabled: nextOpOn, minProfitPct: Number(nextOpPct) || 0.1 },
      };
      void patchActive({ strategyKind, strategyParams: dials });
      const runId = activeRunId;
      const live = activeRunStatus && ['armed', 'signals_only', 'in_position'].includes(activeRunStatus);
      if (runId && live) {
        void api(`/api/strategies/${runId}/live-params`, {
          method: 'PATCH',
          body: JSON.stringify({ params: dials }),
        }).catch(() => null);
      }
    }, 350);
  };

  const marginMetrics = useMemo(() => {
    const bal = account?.balance;
    const positions = account?.positions || [];
    if (!bal && !positions.length) return null;
    const equity = Number(bal?.equity ?? 0);
    const available = Number(bal?.availableMargin ?? bal?.available ?? NaN);
    let notional = 0;
    let posMargin = 0;
    let posMaint = 0;
    for (const p of positions) {
      const size = Math.abs(Number(p.positionAmt ?? p.size ?? p.total ?? 0));
      const mark = Number(p.markPrice ?? p.avgPrice ?? p.entryPrice ?? 0);
      if (size > 0 && mark > 0) notional += size * mark;
      const m = Number(p.margin ?? p.positionMargin ?? p.isolatedMargin ?? 0);
      if (m > 0) posMargin += m;
      const mm = Number(p.maintenanceMargin ?? p.maintMargin ?? 0);
      if (mm > 0) posMaint += mm;
    }
    const balUsed = Number(bal?.usedMargin);
    const derivedUsed = Number.isFinite(equity) && Number.isFinite(available)
      ? Math.max(0, equity - available)
      : 0;
    // Prefer exchange used > 0; else sum of position margins; else equity − available
    const usedMargin = (Number.isFinite(balUsed) && balUsed > 0)
      ? balUsed
      : (posMargin > 0 ? posMargin : (derivedUsed > 1e-8 ? derivedUsed : (Number.isFinite(balUsed) ? balUsed : null)));
    const maint = Number(bal?.maintenanceMargin);
    const maintenanceMargin = (Number.isFinite(maint) && maint > 0)
      ? maint
      : (posMaint > 0 ? posMaint : null);
    return {
      equity: bal?.equity ?? null,
      available: bal?.available ?? null,
      availableMargin: bal?.availableMargin ?? bal?.available ?? null,
      usedMargin,
      maintenanceMargin,
      unrealizedPnl: bal?.unrealizedPnl ?? null,
      marginRatio: bal?.marginRatio ?? null,
      effectiveLeverage: equity > 0 && notional > 0 ? notional / equity : null,
    };
  }, [account]);


  const symbolOptions = useMemo(() => {
    const set = new Set(marketSymbols.length ? marketSymbols : POPULAR_SYMBOLS);
    for (const s of POPULAR_SYMBOLS) set.add(s);
    if (active?.symbol) set.add(active.symbol);
    const all = [...set].sort((a, b) => a.localeCompare(b));
    const q = symbolFilter.trim().toUpperCase();
    if (!q) {
      // Prefer majors first, then full list — select stays usable with 300+ symbols
      const majors = POPULAR_SYMBOLS.filter((s) => set.has(s));
      const rest = all.filter((s) => !POPULAR_SYMBOLS.includes(s));
      return [...majors, ...rest];
    }
    return all.filter((s) => s.includes(q)).slice(0, 80);
  }, [active?.symbol, marketSymbols, symbolFilter]);

  const lastClose = candles.at(-1)?.close;

  const entryForProtective = useMemo(() => {
    if (orderType === 'LIMIT' || orderType === 'STOP') {
      const p = Number(limitPrice);
      if (Number.isFinite(p) && p > 0) return p;
    }
    if (orderType === 'STOP_MARKET') {
      const p = Number(stopPrice);
      if (Number.isFinite(p) && p > 0) return p;
    }
    return lastClose && lastClose > 0 ? lastClose : null;
  }, [orderType, limitPrice, stopPrice, lastClose]);

  const resolveOrderQty = (): number => {
    const raw = Number(qty);
    if (!(raw > 0)) return NaN;
    const px = entryForProtective || lastClose || 0;
    if (sizeMode === 'coins') return raw;
    if (sizeMode === 'usdt') {
      if (!(px > 0)) return NaN;
      return raw / px;
    }
    // % of available deposit (spot) / equity (futures) — spot capped at 98%.
    const dep = Number(account?.balance?.available ?? account?.balance?.equity ?? 0);
    if (!(dep > 0) || !(px > 0)) return NaN;
    const pct = activeMarket === 'spot' ? Math.min(raw, 98) : raw;
    const notional = dep * (pct / 100);
    return notional / px;
  };

  const placeOrder = async () => {
    if (!activeKeyId) { setMsg(t.needKey); setRightTab('keys'); return; }
    if (!active) return;
    setOrderBusy(true);
    try {
      const needsPrice = orderType === 'LIMIT' || orderType === 'STOP';
      const needsStop = orderType === 'STOP' || orderType === 'STOP_MARKET';
      const entry = entryForProtective || Number(limitPrice) || Number(stopPrice) || lastClose || 0;
      const quantity = resolveOrderQty();
      if (!(quantity > 0) || !Number.isFinite(quantity)) {
        setMsg(t.sizeInvalid);
        setOrderBusy(false);
        return;
      }
      const brackets = resolveProtectivePrices(entry, side, protective);
      const data = await api<{ order: unknown; bracket?: Record<string, unknown> }>(`/api/keys/${activeKeyId}/order`, {
        method: 'POST',
        body: JSON.stringify({
          symbol: active.symbol,
          marketType: activeMarket,
          side,
          type: activeMarket === 'spot' && (orderType === 'STOP' || orderType === 'STOP_MARKET')
            ? 'MARKET'
            : orderType,
          quantity,
          price: needsPrice ? Number(limitPrice) : undefined,
          stopPrice: activeMarket === 'spot' ? undefined : (needsStop ? Number(stopPrice) : undefined),
          timeInForce: orderType === 'LIMIT' || orderType === 'STOP' ? timeInForce : undefined,
          takeProfitPrice: activeMarket === 'spot' ? undefined : brackets.tp,
          stopLossPrice: activeMarket === 'spot' ? undefined : brackets.sl,
          trailingCallbackRate: activeMarket === 'spot' ? undefined : brackets.trailCallback,
          trailingActivatePrice: activeMarket === 'spot' ? undefined : brackets.trailActivate,
          positionSide: activeMarket === 'spot' ? 'LONG' : undefined,
        }),
      });
      await refreshAccount(activeKeyId, active.symbol);
      await loadLiveFills();
      const br = data.bracket || {};
      const extras = [
        br.tp ? 'TP' : null,
        br.sl ? 'SL' : null,
        br.trailing ? 'trail' : null,
        br.tpError ? 'TP⚠' : null,
        br.slError ? 'SL⚠' : null,
        br.trailingError ? 'trail⚠' : null,
      ].filter(Boolean).join(' · ');
      setMsg(`${t.orderSent(orderType, side, String(Number(quantity.toPrecision(6))), active.symbol)}${extras ? ` · ${extras}` : ''}`);
    } catch (e) { setMsg((e as Error).message); }
    finally { setOrderBusy(false); }
  };

  const backtestMarkers = useMemo(
    () => sketchBacktestMarkers(candles, strategyKind, active?.trade_range || null, {
      ...strategyParams,
      onlyProfit: { enabled: onlyProfitEnabled, minProfitPct: Number(onlyProfitPct) || 0.1 },
    }),
    [candles, strategyKind, active?.trade_range, strategyParams, onlyProfitEnabled, onlyProfitPct],
  );

  const bookHint = useMemo(() => {
    if (strategyKind !== 'order_book' || candles.length < 40) return null;
    const i = candles.length - 2;
    if (i < 20) return null;
    const snap = bookWatchSnapshot(candles, i, {
      ...strategyParams,
      range: active?.trade_range || null,
    });
    const fmt = (n?: number) => (n != null && Number.isFinite(n) ? fmtPrice(n) : '—');
    const side = snap.side === 'short' ? 'short' : 'long';
    if (snap.mode === 'enter') {
      return t.bookWatchEnter(side, fmt(snap.priceLow), fmt(snap.priceHigh), String(snap.strength ?? '—'));
    }
    if (snap.mode === 'wait') {
      const dist = snap.distPct != null && Number.isFinite(snap.distPct)
        ? snap.distPct.toFixed(2)
        : '—';
      return t.bookWatchWait(side, fmt(snap.priceLow), fmt(snap.priceHigh), dist, String(snap.strength ?? '—'));
    }
    return t.bookWatchNone(snap.freshCount, snap.testedCount);
  }, [strategyKind, candles, strategyParams, active?.trade_range, t]);

  const locale = lang === 'ru' ? 'ru-RU' : 'en-US';
  const toolsBeforeRange: Array<{ id: DrawTool; label: string; hint: string }> = [
    { id: 'pointer', label: t.toolCursor, hint: t.tipCursor },
    { id: 'hline', label: t.toolHline, hint: t.tipHline },
  ];
  const toolsAfterRange: Array<{ id: DrawTool; label: string; hint: string }> = [
    { id: 'trend', label: t.toolTrend, hint: t.tipTrend },
    { id: 'ruler', label: t.toolRuler, hint: t.tipRuler },
    { id: 'eraser', label: t.toolEraser, hint: t.tipEraser },
  ];
  // Classic PC desk (minWidth 980 + 340px aside). Phone: one viewport of chart,
  // swipe right one full viewport to AI Chip / Cabinet (no dead strip after aside).
  const phoneLayout = useMediaQuery('(max-width: 900px)');
  const [phoneW, setPhoneW] = useState(() => (
    typeof window !== 'undefined' ? Math.max(320, window.innerWidth || 390) : 390
  ));
  useEffect(() => {
    if (!phoneLayout) return;
    const measure = () => {
      const w = document.getElementById('root')?.clientWidth || window.innerWidth || 390;
      setPhoneW(Math.max(320, Math.round(w)));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    const root = document.getElementById('root');
    if (root && ro) ro.observe(root);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [phoneLayout]);
  const phoneAsideW = phoneLayout ? phoneW : asideWidth;
  const phoneDeskW = phoneLayout ? phoneW + phoneAsideW : 980;
  const desktopSplit = !phoneLayout && !chartFocus && termDock === 'split';
  const gridRows = phoneLayout ? '40px auto minmax(0, auto)' : '48px 40px 1fr';

  const toolBtn = (toolItem: { id: DrawTool; label: string; hint: string }) => (
    <button
      key={toolItem.id}
      type="button"
      title={toolItem.hint}
      onClick={() => setTool(toolItem.id)}
      style={{
        ...tabBtn,
        // Phone: flex-shrink was crushing labels onto 2 lines → 58px-tall tool chips.
        flexShrink: 0,
        whiteSpace: 'nowrap',
        padding: phoneLayout ? '3px 7px' : '5px 9px',
        fontSize: phoneLayout ? 11 : 12,
        fontWeight: 500,
        letterSpacing: '0.01em',
        lineHeight: 1.25,
        borderColor: tool === toolItem.id ? 'var(--gold)' : 'var(--line)',
        color: tool === toolItem.id ? 'var(--gold)' : 'var(--text)',
        background: tool === toolItem.id ? 'rgba(212,175,55,0.12)' : 'var(--panel)',
      }}
    >
      {toolItem.label}
    </button>
  );
  // REAL fill ticks only. Sketch stays as markers — dumping 40+ sketch LineSeries
  // into the chart on every candle poll was resetting the time scale to the left.
  const levelDashes = useMemo(
    () => buildFillLevelDashes(closedTrades as any),
    [closedTrades],
  );
  // Stable sketch ticks (content-keyed) so ChartPane can draw price points without
  // thrashing when live candles only refresh the forming bar.
  const sketchDashes = useMemo(() => {
    if (!active?.trade_range) return [] as Array<{ time: number; price: number; title: string; color: string; kind?: string }>;
    return backtestMarkers.map((m) => {
      const exit = m.side === 'exit';
      const add = Boolean(m.label && String(m.label).includes('+'));
      return {
        time: Number(m.time) || 0,
        price: Number(m.price) || 0,
        kind: exit ? 'exit' : add ? 'add' : 'entry',
        title: `sk ${m.label}`,
        // Soft cyan / peach — not candle green/red, so bars stay readable.
        color: exit ? '#d4b85a' : m.side === 'long' ? '#6ec8ff' : '#f0a070',
      };
    });
  }, [active?.trade_range, backtestMarkers]);
  const sketchDashKey = useMemo(
    () => sketchDashes.map((d) => `${d.time}:${d.price.toFixed(8)}:${d.kind}`).join('|'),
    [sketchDashes],
  );
  const activeOpenPos = useMemo((): OpenPosition | null => {
    const list = account?.positions || [];
    const activeSym = String(active?.symbol || '').toUpperCase().replace(/_/g, '');
    for (const raw of list) {
      const n = normalizeOpenPosition(raw);
      if (!n) continue;
      const sym = n.symbol.replace(/_/g, '');
      if (sym === activeSym || n.symbol === active?.symbol) return n;
    }
    return null;
  }, [account?.positions, active?.symbol]);

  const selectedOpenPos = useMemo((): OpenPosition | null => {
    if (!selectedPosKey) return null;
    const list = account?.positions || [];
    for (const raw of list) {
      const n = normalizeOpenPosition(raw);
      if (n && positionKey(n) === selectedPosKey) return n;
    }
    return null;
  }, [account?.positions, selectedPosKey]);

  // Drop selection when the position is gone
  useEffect(() => {
    if (!selectedPosKey) return;
    const still = (account?.positions || []).some((raw: any) => {
      const n = normalizeOpenPosition(raw);
      return n && positionKey(n) === selectedPosKey;
    });
    if (!still) setSelectedPosKey(null);
  }, [account?.positions, selectedPosKey]);

  const avgEntryLevel = useMemo(() => {
    if (!activeOpenPos) return null;
    const short = activeOpenPos.side === 'short';
    const selected = selectedPosKey === positionKey(activeOpenPos);
    return {
      price: activeOpenPos.entryPrice,
      title: short ? 'avg S' : 'avg L',
      color: short ? '#e25b5b' : '#3dba7a',
      selected,
      side: activeOpenPos.side,
    };
  }, [activeOpenPos, selectedPosKey]);

  const selectPosition = useCallback((key: string | null) => {
    setSelectedPosKey(key);
    if (key) {
      setClosePct(100);
      setPosProtective(defaultProtective());
      setTermTab('positions');
      setChartFocus(false);
    }
  }, []);

  const closeSelectedPct = async () => {
    if (!activeKeyId || !selectedOpenPos) {
      setMsg(t.needKey);
      setRightTab('keys');
      return;
    }
    const pct = Math.max(1, Math.min(100, Math.round(closePct)));
    const qty = selectedOpenPos.size * (pct / 100);
    if (!(qty > 0)) return;
    const closeSide = selectedOpenPos.side === 'long' ? 'sell' : 'buy';
    const positionSide = selectedOpenPos.side === 'long' ? 'LONG' : 'SHORT';
    setPosBusy(true);
    try {
      await api(`/api/keys/${activeKeyId}/order`, {
        method: 'POST',
        body: JSON.stringify({
          symbol: selectedOpenPos.symbol,
          side: closeSide,
          type: 'MARKET',
          quantity: qty,
          reduceOnly: true,
          positionSide,
        }),
      });
      await refreshAccount(activeKeyId, active?.symbol);
      await loadLiveFills();
      setMsg(t.posClosed(pct, selectedOpenPos.symbol));
      if (pct >= 100) setSelectedPosKey(null);
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setPosBusy(false);
    }
  };

  const attachProtectiveToSelected = async () => {
    if (!activeKeyId || !selectedOpenPos) {
      setMsg(t.needKey);
      setRightTab('keys');
      return;
    }
    const orderSide = selectedOpenPos.side === 'long' ? 'buy' : 'sell';
    const positionSide = selectedOpenPos.side === 'long' ? 'LONG' : 'SHORT';
    const brackets = resolveProtectivePrices(selectedOpenPos.entryPrice, orderSide, posProtective);
    if (brackets.tp == null && brackets.sl == null && brackets.trailCallback == null) {
      setMsg(t.posNeedProtective);
      return;
    }
    setPosBusy(true);
    try {
      const data = await api<{ bracket?: Record<string, unknown> }>(`/api/keys/${activeKeyId}/protective`, {
        method: 'POST',
        body: JSON.stringify({
          symbol: selectedOpenPos.symbol,
          positionSide,
          quantity: selectedOpenPos.size,
          takeProfitPrice: brackets.tp,
          stopLossPrice: brackets.sl,
          trailingCallbackRate: brackets.trailCallback,
          trailingActivatePrice: brackets.trailActivate,
        }),
      });
      const br = data.bracket || {};
      const extras = [
        br.tp ? 'TP' : null,
        br.sl ? 'SL' : null,
        br.trailing ? 'trail' : null,
        br.tpError ? 'TP⚠' : null,
        br.slError ? 'SL⚠' : null,
        br.trailingError ? 'trail⚠' : null,
      ].filter(Boolean).join(' · ');
      setMsg(`${t.posProtectiveAttached(selectedOpenPos.symbol)}${extras ? ` · ${extras}` : ''}`);
      await refreshAccount(activeKeyId, active?.symbol);
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setPosBusy(false);
    }
  };

  const positionToolsNode = selectedOpenPos ? (
    <PositionTools
      position={selectedOpenPos}
      closePct={closePct}
      onClosePct={setClosePct}
      protective={posProtective}
      onProtective={setPosProtective}
      busy={posBusy}
      onClose={() => void closeSelectedPct()}
      onAttachProtective={() => void attachProtectiveToSelected()}
      onDismiss={() => setSelectedPosKey(null)}
      labels={{
        closeBtn: t.posCloseBtn,
        attachProtective: t.posAttachProtective,
        moreProtective: t.posMoreProtective,
        lessProtective: t.posLessProtective,
        dismiss: t.posDismiss,
        busy: t.posBusy,
      }}
    />
  ) : null;

  return (
    <div ref={deskRef} style={{
      height: phoneLayout ? 'auto' : '100%',
      minHeight: '100%',
      // Phone: exactly 2 viewports (chart | Chip/Cabinet). No 100vw scrollbar phantom gap.
      width: chartFocus ? '100%' : (phoneLayout ? phoneDeskW : '100%'),
      minWidth: chartFocus ? '100%' : (phoneLayout ? phoneDeskW : 980),
      maxWidth: phoneLayout && !chartFocus ? phoneDeskW : undefined,
      // Firefox: overflow:hidden + classic scrollbar ate ~15px of the right aside / Stop chip.
      overflow: phoneLayout ? 'visible' : 'auto',
      scrollbarGutter: phoneLayout ? undefined : 'stable',
      display: 'grid', gridTemplateRows: gridRows, gap: 0,
      position: 'relative',
      touchAction: 'pan-x pan-y',
      boxSizing: 'border-box',
    }}>
      {paywall ? (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.55)',
          display: 'grid', placeItems: 'center', padding: 16,
        }}>
          <div style={{
            width: 'min(420px, 100%)', background: 'var(--panel)', border: '1px solid var(--gold)',
            borderRadius: 14, padding: 18, boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
          }}>
            <div style={{ fontFamily: 'var(--display)', fontSize: 26, color: 'var(--gold)' }}>{t.upgradeTitle}</div>
            <div style={{
              marginTop: 10, padding: '8px 12px', borderRadius: 10,
              border: '1px solid var(--gold)', background: 'rgba(223,184,58,0.08)',
              fontSize: 13, color: 'var(--gold)', fontWeight: 650,
            }}>
              {t.planNow}: {plan?.name || me?.planId || 'Free'} · {plan?.tabLimit || PLANS.free.tabLimit} {t.tabs}
            </div>
            <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
              {t.planTabsCaption}
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
              {t.volumeUnlockHint}
            </p>
            {(() => {
              const vol = Number(plan?.tradedVolumeUsdt || 0);
              const { nextAt, nextTabs } = nextVolumeUnlock(vol);
              return (
                <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--gold)', lineHeight: 1.4 }}>
                  {t.volumeUnlockProgress(vol, nextAt, nextTabs)}
                </p>
              );
            })()}
            <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text)', lineHeight: 1.45 }}>
              {t.tabLimitPay}
            </p>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
              {t.upgradeHint(paywall.quote.plan?.name || paywall.suggestedPlanId, paywall.quote.amountUsdt)}
            </p>
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>{t.depositWallet}</div>
            <div style={{
              marginTop: 4, fontFamily: 'var(--mono)', fontSize: 12, wordBreak: 'break-all',
              color: paywall.wallet.address ? 'var(--gold)' : 'var(--short)', lineHeight: 1.4,
            }}>
              {paywall.wallet.address || t.depositWalletPending}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text)' }}>
              {paywall.quote.amountUsdt} USDT · {t.platformFee}: {paywall.quote.platformFeeUsdt} USDT
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 14 }}>
              <button type="button" style={ghostBtn} onClick={() => setPaywall(null)}>{t.cancel}</button>
              <button
                type="button"
                style={{ ...primaryBtn, marginTop: 0 }}
                onClick={() => void tryActivatePlan(paywall.suggestedPlanId)}
              >{t.iPaid}</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button type="button" style={{ ...ghostBtn, flex: 1 }} onClick={() => void openUpgrade('pro')}>
                {t.pro} · {PLANS.pro.tabLimit} {t.tabs}
              </button>
              <button type="button" style={{ ...ghostBtn, flex: 1 }} onClick={() => void openUpgrade('desk')}>
                {t.desk} · {PLANS.desk.tabLimit} {t.tabs}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {stopDialog ? (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 85, background: 'rgba(0,0,0,0.55)',
          display: 'grid', placeItems: 'center', padding: 16,
        }}>
          <div style={{
            width: 'min(440px, 100%)', background: 'var(--panel)', border: '1px solid var(--gold)',
            borderRadius: 14, padding: 18, boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
          }}>
            <div style={{ fontFamily: 'var(--display)', fontSize: 24, color: 'var(--gold)' }}>
              {t.stopDialogTitle}
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
              {t.stopDialogHint}
            </p>
            <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
              <button
                type="button"
                disabled={stopBusy}
                onClick={() => void stopStrategy('close')}
                style={{ ...dangerBtn, marginTop: 0, textAlign: 'left', padding: '12px 14px' }}
              >
                <div style={{ fontWeight: 700 }}>{t.stopModeClose}</div>
                <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.85, marginTop: 4 }}>{t.stopModeCloseHint}</div>
              </button>
              <button
                type="button"
                disabled={stopBusy}
                onClick={() => void stopStrategy('leave')}
                style={{
                  ...ghostBtn, textAlign: 'left', padding: '12px 14px',
                  borderColor: 'var(--gold)', color: 'var(--text)',
                }}
              >
                <div style={{ fontWeight: 700, color: 'var(--gold)' }}>{t.stopModeLeave}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{t.stopModeLeaveHint}</div>
              </button>
              <button
                type="button"
                disabled={stopBusy}
                onClick={() => void stopStrategy('soft')}
                style={{
                  ...ghostBtn, textAlign: 'left', padding: '12px 14px',
                  borderColor: 'var(--line)', color: 'var(--text)',
                }}
              >
                <div style={{ fontWeight: 700 }}>{t.stopModeSoft}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{t.stopModeSoftHint}</div>
              </button>
              <button
                type="button"
                disabled={stopBusy}
                onClick={() => setStopDialog(false)}
                style={{ ...ghostBtn, marginTop: 4 }}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {drawingToolsOn && deskCross ? (
        <>
          <div style={{
            position: 'absolute', left: deskCross.x, top: 0, bottom: 0, width: 1, zIndex: 3,
            background: 'rgba(232,237,245,0.45)', pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', top: deskCross.y, left: 0, right: 0, height: 1, zIndex: 3,
            background: 'rgba(232,237,245,0.45)', pointerEvents: 'none',
          }} />
        </>
      ) : null}
      <header style={{
        display: 'flex', alignItems: 'center', gap: phoneLayout ? 8 : 12,
        padding: phoneLayout ? '0 8px' : '0 12px',
        borderBottom: '1px solid var(--line)', background: 'var(--header-bg)', backdropFilter: 'blur(8px)',
        minHeight: phoneLayout ? 40 : 48, flexShrink: 0,
        // Phone: pin header to the chart viewport so it cannot widen scrollWidth past the aside.
        position: phoneLayout ? 'sticky' : 'relative',
        left: phoneLayout ? 0 : undefined,
        width: phoneLayout ? phoneW : '100%',
        maxWidth: phoneLayout ? phoneW : '100%',
        boxSizing: 'border-box',
        // Firefox classic scrollbar + many header chips used to clip Stop AI Chip on the right.
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        // Above main desk (zIndex 5) so language menu receives clicks
        zIndex: 60,
      }}>
        <div style={{ fontFamily: 'var(--display)', fontSize: phoneLayout ? 22 : 28, color: 'var(--gold)' }}>Termix</div>
        <div style={{
          color: 'var(--text)', fontSize: phoneLayout ? 11 : 13,
          display: phoneLayout ? 'none' : 'flex',
          alignItems: 'center', gap: 8, flexWrap: 'wrap', opacity: 0.82,
        }}>
          <span>
            {t.brandSub} · {status === 'live' ? t.live : status === 'offline' ? t.offline : status}
          </span>
          <span
            title={(() => {
              const vol = Number(plan?.tradedVolumeUsdt || 0);
              const { nextAt, nextTabs } = nextVolumeUnlock(vol);
              return `${t.planTabsHint}. ${t.volumeUnlockHint}. ${t.volumeUnlockProgress(vol, nextAt, nextTabs)}`;
            })()}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 10px', borderRadius: 8,
              border: '1px solid var(--gold)', color: 'var(--gold)',
              background: 'rgba(223,184,58,0.08)', fontSize: 12, fontWeight: 650,
            }}
          >
            {t.planNow}: {plan?.name || me?.planId || '…'} · {plan?.tabLimit || PLANS.free.tabLimit} {t.tabs}
            {Number(plan?.volumeUnlockedTabs || 0) > Number(plan?.baseTabLimit || 0) ? (
              <span style={{ opacity: 0.8, fontWeight: 500 }}>· vol</span>
            ) : null}
          </span>
        </div>
        <div style={{
          marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center',
          flexShrink: 0, minWidth: 0, paddingRight: 2,
        }}>
          <span
            title={
              netQuality === 'stable' ? t.netStable
                : netQuality === 'weak' ? t.netWeak
                  : netQuality === 'offline' ? t.netOffline
                    : t.netConnecting
            }
            style={{
              fontSize: 11, fontFamily: 'var(--mono)', padding: '3px 8px', borderRadius: 6,
              border: '1px solid var(--line)',
              color: netQuality === 'stable' ? 'var(--long)'
                : netQuality === 'weak' ? 'var(--gold)'
                  : netQuality === 'offline' ? 'var(--short)' : 'var(--muted)',
              background: 'var(--panel-2)',
            }}
          >
            {netQuality === 'stable' ? `● ${t.netStable}`
              : netQuality === 'weak' ? `◐ ${t.netWeak}`
                : netQuality === 'offline' ? `○ ${t.netOffline}`
                  : `… ${t.netConnecting}`}
          </span>
          <div ref={langMenuRef} style={{ position: 'relative', zIndex: 70 }}>
            <button
              type="button"
              title={t.langMenu}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setLangOpen((v) => !v); }}
              style={{ ...ghostBtn, fontFamily: 'var(--mono)', minWidth: 52, display: 'inline-flex', gap: 6, alignItems: 'center' }}
            >
              <span aria-hidden>🌐</span>
              {lang === 'en' ? 'EN' : 'RU'}
            </button>
            {langOpen ? (
              <div
                role="menu"
                style={{
                  position: 'absolute', right: 0, top: '110%', zIndex: 80,
                  minWidth: 148, padding: 6, borderRadius: 10,
                  border: '1px solid var(--line)', background: 'var(--panel)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--muted)', padding: '4px 8px' }}>{t.langMenu}</div>
                {([
                  ['en', t.langEn],
                  ['ru', t.langRu],
                ] as const).map(([code, label]) => (
                  <button
                    key={code}
                    type="button"
                    role="menuitem"
                    onMouseDown={(e) => {
                      // mousedown: beat the desk layer that was stealing the click
                      e.preventDefault();
                      e.stopPropagation();
                      setLang(code);
                      setLangOpen(false);
                    }}
                    style={{
                      ...ghostBtn, width: '100%', textAlign: 'left', marginTop: 2, cursor: 'pointer',
                      borderColor: lang === code ? 'var(--gold)' : 'transparent',
                      color: lang === code ? 'var(--gold)' : 'var(--text)',
                    }}
                  >{label}</button>
                ))}
              </div>
            ) : null}
          </div>
          <Link to="/help" style={{ color: 'var(--text)', opacity: 0.75, textDecoration: 'none', fontSize: 13 }}>{t.help}</Link>
          <button
            type="button"
            title={t.themeToggleTitle}
            onClick={() => setTheme(theme === 'dark' ? 'ivory' : 'dark')}
            style={{
              ...ghostBtn,
              borderColor: theme === 'ivory' ? 'var(--gold)' : 'var(--line)',
              color: theme === 'ivory' ? 'var(--gold)' : 'var(--text)',
            }}
          >
            {theme === 'ivory' ? `◇ ${t.themeIvory}` : `◆ ${t.themeDark}`}
          </button>
          <button
            type="button"
            title={chartFocus ? t.showPanels : t.chartFocus}
            onClick={() => setChartFocus((v) => {
              const next = !v;
              if (next) setTermDock('chart');
              return next;
            })}
            style={ghostBtn}
          >
            {chartFocus ? `☰ ${t.chartFocusOff}` : `⛶ ${t.chartFocus}`}
          </button>
          <button
            type="button"
            title={t.cabinet}
            onClick={() => { setChartFocus(false); setRightTab('keys'); }}
            style={{
              ...ghostBtn,
              borderColor: rightTab === 'keys' ? 'var(--gold)' : 'var(--line)',
              color: rightTab === 'keys' ? 'var(--gold)' : 'var(--text)',
            }}
          >
            {t.cabinet}
          </button>
          {me?.isAdmin ? (
            <Link to="/admin" style={{ color: 'var(--muted)', textDecoration: 'none', fontSize: 13 }}>{t.admin}</Link>
          ) : null}
          <button type="button" onClick={() => { clearToken(); location.href = '/login'; }} style={ghostBtn}>{t.logout}</button>
          <button
            type="button"
            style={dangerBtn}
            title={t.stopStrategiesTitle}
            onClick={async () => {
              if (!window.confirm(t.stopStrategiesConfirm)) return;
              await api('/api/strategies/kill-switch', { method: 'POST' });
              setActiveRunId(null);
              setLiveRuns([]);
              setMsg(t.stopStrategiesDone);
            }}
          >⏹ {t.stopStrategies}</button>
        </div>
      </header>

      <div style={{
        display: 'flex', gap: phoneLayout ? 6 : 8,
        padding: phoneLayout ? '2px 6px' : '4px 8px 4px 10px',
        borderBottom: '1px solid var(--line)',
        alignItems: 'stretch', minHeight: 0,
        position: phoneLayout ? 'sticky' : undefined,
        left: phoneLayout ? 0 : undefined,
        width: phoneLayout ? phoneW : undefined,
        maxWidth: phoneLayout ? phoneW : undefined,
        boxSizing: 'border-box',
        background: phoneLayout ? 'var(--bg)' : undefined,
        zIndex: phoneLayout ? 55 : undefined,
      }}>
        <div style={{
          flex: 1, minWidth: 0, display: 'flex', gap: 6, alignItems: 'center',
          overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin', paddingBottom: 2,
        }}>
          {workspaces.map((w) => {
            const live = liveByWorkspace.get(w.id);
            // Live status stays green/gold; time-ending is a separate orange pulse (not red “error”).
            const liveColor = live?.status === 'in_position' ? 'var(--gold)'
              : live ? 'var(--long)' : null;
            const tr = w.trade_range;
            const tfSec = timeframeSeconds(w.timeframe || '15m');
            const ending = rangeTimeEndingSoon(tr || null, nowTick, tfSec);
            const warnTip = ending.soon
              ? (Number.isFinite(ending.leftSec)
                ? t.rangeTimeEndingLeft(
                  ending.leftSec >= 3600
                    ? `${Math.floor(ending.leftSec / 3600)}:${String(Math.floor((ending.leftSec % 3600) / 60)).padStart(2, '0')}`
                    : `${String(Math.floor(ending.leftSec / 60)).padStart(2, '0')}:${String(ending.leftSec % 60).padStart(2, '0')}`,
                )
                : t.rangeTimeEnding)
              : undefined;
            const tabLiveTitle = warnTip || (live ? t.stratTabLive : undefined);
            return (
            <div
              key={w.id}
              className={ending.soon && live ? 'termix-tab-time-warn' : undefined}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 2, borderRadius: 8,
                flexShrink: 0,
                border: w.id === active?.id
                  ? '2px solid var(--gold)'
                  : `1px solid ${
                    ending.soon && live ? '#e88c30'
                      : live ? 'var(--long)' : 'var(--line)'
                  }`,
                background: w.id === active?.id
                  ? 'rgba(201,162,39,0.18)'
                  : 'var(--panel-2)',
                boxShadow: w.id === active?.id
                  ? 'inset 3px 0 0 var(--gold), 0 0 0 1px rgba(201,162,39,0.25)'
                  : undefined,
              }}
              title={tabLiveTitle}
            >
              <button onClick={() => setActiveId(w.id)} title={tabLiveTitle} style={{
                ...tabBtn, border: 'none', borderRadius: '8px 0 0 8px',
                color: w.id === active?.id ? 'var(--gold)' : 'var(--text)',
                background: 'transparent',
                fontWeight: w.id === active?.id ? 700 : 500,
                display: 'inline-flex', alignItems: 'center', gap: 5,
                whiteSpace: 'nowrap', padding: '5px 7px', fontSize: 12,
              }}>
                {liveColor ? (
                  <span
                    className={ending.soon ? 'termix-live-dot-warn' : undefined}
                    style={{
                      width: 7, height: 7, borderRadius: 99,
                      background: liveColor,
                      boxShadow: ending.soon
                        ? '0 0 0 2px #e88c30, 0 0 8px #e88c30'
                        : `0 0 6px ${liveColor}`,
                      flexShrink: 0,
                    }}
                    title={tabLiveTitle}
                  />
                ) : null}
                <span style={{ fontFamily: w.id === active?.id ? 'var(--mono)' : undefined }}>
                  {w.symbol}
                </span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: 'var(--mono)',
                  opacity: w.id === active?.id ? 1 : 0.75,
                  color: w.id === active?.id ? 'var(--gold)' : 'var(--muted)',
                }}>
                  {w.timeframe}
                </span>
                {live?.kind ? (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: 0.02,
                      padding: '1px 4px',
                      borderRadius: 4,
                      border: `1px solid ${ending.soon ? 'rgba(232,140,48,0.55)' : 'rgba(61,186,122,0.45)'}`,
                      color: ending.soon ? '#e88c30' : 'var(--long)',
                      background: ending.soon ? 'rgba(232,140,48,0.1)' : 'rgba(61,186,122,0.1)',
                      fontFamily: 'var(--mono)',
                    }}
                    title={`${t.stratTabLive} · ${chipCopy(t, live.kind).name}`}
                  >
                    {chipTabTag(t, live.kind)}
                  </span>
                ) : live ? (
                  <span
                    style={{
                      fontSize: 10,
                      color: ending.soon ? '#e88c30' : (liveColor || 'var(--long)'),
                      fontWeight: 700,
                    }}
                    title={tabLiveTitle}
                  >
                    {t.stratTabLive}
                  </span>
                ) : null}
              </button>
              <button title={t.closeTab} onClick={(e) => void closeTab(w.id, e)} style={{
                border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', padding: '6px 8px',
              }}>×</button>
            </div>
            );
          })}
        </div>
        <div style={{
          flexShrink: 0, display: 'flex', gap: 5, alignItems: 'center',
          paddingLeft: phoneLayout ? 4 : 8, marginLeft: 2,
          borderLeft: '1px solid var(--line)',
        }}>
          <button
            onClick={() => void addTab()}
            style={{ ...tabBtn, color: 'var(--gold)', flexShrink: 0, fontSize: 12, padding: phoneLayout ? '4px 7px' : '5px 8px' }}
          >
            {t.addTab}
          </button>
          {/* Phone: plan chips live in header / Cabinet — keep the tab strip for pairs only. */}
          {!phoneLayout ? (
            <>
              <span
                style={{ fontSize: 10, color: 'var(--muted)', lineHeight: 1.15, maxWidth: 88, whiteSpace: 'normal' }}
                title={`${t.planTabsHint}. ${t.volumeUnlockHint}`}
              >
                {t.planTabsCaption}
              </span>
              {([
                ['free', t.planBtnFree] as const,
                ['pro', t.pro] as const,
                ['desk', t.desk] as const,
              ]).map(([id, label]) => {
                const cur = (plan?.id || me?.planId || 'free') === id;
                const tabs = PLANS[id as keyof typeof PLANS]?.tabLimit ?? 0;
                return (
                  <button
                    key={id}
                    type="button"
                    title={`${label} · ${tabs} ${t.tabs}`}
                    onClick={() => { if (id === 'free') return; void upgrade(id); }}
                    style={{
                      ...ghostBtn,
                      borderColor: cur ? 'var(--gold)' : 'var(--line)',
                      color: cur ? 'var(--gold)' : 'var(--text)',
                      background: cur ? 'rgba(223,184,58,0.1)' : 'transparent',
                      boxShadow: cur ? '0 0 0 1px rgba(223,184,58,0.35)' : 'none',
                      fontWeight: cur ? 700 : 500,
                      cursor: id === 'free' ? 'default' : 'pointer',
                      fontSize: 11,
                      padding: '4px 7px',
                      flexShrink: 0,
                    }}
                  >
                    {label}
                    <span style={{ opacity: 0.75, marginLeft: 3 }}>· {tabs}</span>
                  </button>
                );
              })}
            </>
          ) : null}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: chartFocus
          ? '1fr'
          : (phoneLayout
            ? `${phoneW}px ${phoneAsideW}px`
            : `minmax(0, 1fr) 8px ${asideWidth}px`),
        // Phone pans one full viewport to Chip/Cabinet — never stretch chart to aside height.
        alignItems: phoneLayout ? 'start' : 'stretch',
        minHeight: phoneLayout ? undefined : 0,
        height: phoneLayout ? 'auto' : '100%',
        // Desktop: avoid clipping the right Chip column (Firefox scrollbar + overflow:hidden).
        overflow: phoneLayout ? 'visible' : 'auto',
        position: 'relative', zIndex: 5,
        boxSizing: 'border-box',
      }}>
        <div style={{
          minWidth: 0,
          minHeight: phoneLayout ? undefined : 0,
          alignSelf: phoneLayout ? 'start' : 'stretch',
          padding: phoneLayout ? '4px 6px 6px' : 8,
          display: 'grid',
          alignContent: 'start',
          // Phone: fixed min chart + basement so #root can scroll down past the fold.
          // Desktop split: chart is the only flexible row; basement is stored px (Mac-stable).
          gridTemplateRows: phoneLayout
            ? (chartFocus
              ? 'auto auto minmax(280px, 58vh)'
              : termDock === 'chart'
                ? 'auto auto minmax(280px, 58vh) 32px'
                : termDock === 'terminal'
                  ? 'auto auto 32px minmax(240px, 50vh)'
                  : 'auto auto minmax(280px, 48vh) minmax(200px, 38vh)')
            : (chartFocus
              ? 'auto auto minmax(0, 1fr)'
              : termDock === 'chart'
                ? 'auto auto minmax(0, 1fr) 36px'
                : termDock === 'terminal'
                  ? 'auto auto 36px minmax(0, 1fr)'
                  : `auto auto minmax(0, 1fr) 8px ${basementH}px`),
          gap: phoneLayout ? 4 : 6,
          overflow: phoneLayout ? 'visible' : 'hidden',
        }}>
          <div style={{
            display: 'flex',
            flexWrap: phoneLayout ? 'nowrap' : 'wrap',
            gap: phoneLayout ? 6 : 8,
            alignItems: 'center',
            alignSelf: 'start',
            minWidth: 0,
            minHeight: 0,
            fontSize: 12,
            letterSpacing: '0.01em',
          }}>
            {!phoneLayout ? (
              <label style={{ fontSize: 11, color: 'var(--muted)', flexShrink: 0, fontWeight: 500 }} title={t.pair}>
                {t.pair}
              </label>
            ) : null}
            <PairPicker
              value={active?.symbol || 'BTCUSDT'}
              options={symbolOptions}
              filter={symbolFilter}
              onFilter={setSymbolFilter}
              onPick={(sym) => void changeSymbol(sym)}
              marketType={activeMarket}
              onMarketType={(m) => void changeMarketType(m)}
            />
            {/* Phone: PairPicker alone — customSymbol mirrors active and looked like a duplicate HYPEUSDT. */}
            {!phoneLayout ? (
              <>
                <input
                  value={customSymbol}
                  onChange={(e) => setCustomSymbol(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter') void changeSymbol(customSymbol); }}
                  placeholder="BTCUSDT"
                  title={t.otherPair}
                  style={{ ...compactInput, width: 120 }}
                />
                <button
                  type="button"
                  title={t.changePairTitle}
                  onClick={() => void changeSymbol(customSymbol)}
                  style={ghostBtn}
                >{t.changePair}</button>
              </>
            ) : null}
            <div
              style={{
                display: 'flex',
                gap: 4,
                flexWrap: 'nowrap',
                overflowX: phoneLayout ? 'auto' : 'visible',
                WebkitOverflowScrolling: 'touch',
                minWidth: 0,
                flex: phoneLayout ? '1 1 auto' : undefined,
                paddingBottom: phoneLayout ? 1 : 0,
              }}
              role="group"
              aria-label={t.tfTitle(active?.timeframe || '15m')}
            >
              {TERMIX_TIMEFRAMES.map((tf) => {
                const on = active?.timeframe === tf;
                return (
                  <button
                    key={tf}
                    type="button"
                    title={t.tfTitle(tf)}
                    aria-pressed={on}
                    onClick={() => {
                      if (!active || active.timeframe === tf) return;
                      const title = `${active.symbol} ${tf}`;
                      setWorkspaces((list) => list.map((w) => (w.id === active.id ? { ...w, timeframe: tf, title } : w)));
                      void patchActive({ timeframe: tf as TermixTimeframe, title });
                      pushDeskHistory(makeDeskSnap({ timeframe: tf }));
                      // Live chip follows the tab TF (engine reloads run.timeframe each cycle).
                      const runId = activeLiveRun?.id || activeRunId;
                      if (runId && activeLiveRun && ['armed', 'signals_only', 'in_position'].includes(activeLiveRun.status)) {
                        void api(`/api/strategies/${runId}/timeframe`, {
                          method: 'PATCH',
                          body: JSON.stringify({ timeframe: tf }),
                        }).then(() => refreshRuns()).catch((e) => setMsg((e as Error).message));
                      }
                    }}
                    style={{
                      ...tabBtn,
                      minWidth: phoneLayout ? 36 : 40,
                      flexShrink: 0,
                      padding: phoneLayout ? '4px 6px' : '5px 8px',
                      fontWeight: on ? 600 : 500,
                      fontFamily: 'var(--mono)',
                      fontSize: phoneLayout ? 11 : 12,
                      letterSpacing: '0.02em',
                      borderWidth: 1,
                      borderColor: on ? 'var(--gold)' : 'var(--line)',
                      color: on ? '#1a1408' : 'var(--text)',
                      background: on
                        ? 'linear-gradient(180deg,#e6c65a,var(--gold))'
                        : 'var(--panel-2)',
                      boxShadow: on ? '0 0 0 1px rgba(201,162,39,0.28)' : undefined,
                    }}
                  >{tf}</button>
                );
              })}
            </div>
            {!phoneLayout && msg ? <span style={{ color: 'var(--muted)', fontSize: 12, maxWidth: 480 }}>{msg}</span> : null}
          </div>

          <div style={{
            display: 'flex',
            flexWrap: phoneLayout ? 'nowrap' : 'wrap',
            gap: phoneLayout ? 4 : 6,
            alignItems: 'center',
            alignSelf: 'start',
            padding: phoneLayout ? '3px 5px' : '5px 8px',
            borderRadius: 8,
            border: '1px solid var(--line)',
            background: 'var(--panel-2)',
            overflowX: phoneLayout ? 'auto' : 'visible',
            WebkitOverflowScrolling: 'touch',
            minWidth: 0,
            minHeight: 0,
            position: 'relative',
            fontSize: 12,
            letterSpacing: '0.01em',
          }}>
            {!phoneLayout ? (
              <span style={{ fontSize: 10, color: 'var(--muted)', marginRight: 4, flexShrink: 0, fontWeight: 600, letterSpacing: '0.06em' }}>{t.drawing}</span>
            ) : null}
            {toolsBeforeRange.map(toolBtn)}
            {/* Range + by-time: one quiet group so time binding is clearly for the box */}
            <div
              title={t.byTimeTitle}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '2px 5px',
                borderRadius: 7,
                border: '1px solid rgba(201,162,39,0.22)',
                background: 'rgba(201,162,39,0.04)',
              }}
            >
              {toolBtn({ id: 'range', label: t.toolRange, hint: t.tipRange })}
              <label
                style={{
                  display: 'flex', gap: 4, alignItems: 'center',
                  fontSize: 11, color: rangeUseTime ? 'var(--gold)' : 'var(--muted)',
                  cursor: 'pointer', userSelect: 'none', paddingRight: 2,
                }}
                title={t.byTimeTitle}
              >
                <input
                  type="checkbox"
                  checked={rangeUseTime}
                  onChange={(e) => setRangeUseTime(e.target.checked)}
                />
                {t.byTime}
              </label>
            </div>
            {toolsAfterRange.map(toolBtn)}
            <div ref={indFlyoutRef} style={{ position: 'relative', flexShrink: 0 }}>
              <button
                type="button"
                title={t.indicatorsHintOnly}
                aria-expanded={indOpen}
                onClick={() => { setIndOpen((o) => !o); setColorOpen(false); }}
                style={{
                  ...tabBtn,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  padding: phoneLayout ? '3px 7px' : '5px 9px',
                  fontSize: phoneLayout ? 11 : 12,
                  fontWeight: 500,
                  borderColor: indOpen ? 'var(--gold)' : 'var(--line)',
                  color: indOpen ? 'var(--gold)' : 'var(--text)',
                  background: indOpen ? 'rgba(212,175,55,0.12)' : 'var(--panel)',
                }}
              >
                {t.indicators}
              </button>
              {indOpen ? (
                <div
                  role="dialog"
                  aria-label={t.indicatorsTitle}
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    left: phoneLayout ? 0 : '50%',
                    transform: phoneLayout ? undefined : 'translateX(-50%)',
                    zIndex: 80,
                    width: phoneLayout ? 'min(92vw, 360px)' : 360,
                    maxHeight: phoneLayout ? 'min(70vh, 520px)' : 520,
                    overflow: 'auto',
                    padding: 12,
                    borderRadius: 12,
                    border: '1px solid var(--line)',
                    background: 'var(--panel)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                  }}
                >
                  <IndicatorsPanel
                    payload={payload}
                    onPayloadChange={onPayloadChange}
                    onSave={() => void saveDrawings()}
                    candles={candles}
                    timeframe={active?.timeframe || '15m'}
                    onNotice={setMsg}
                  />
                </div>
              ) : null}
            </div>
            <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 4 }}>{t.color}</span>
            <button
              type="button"
              title={t.color}
              aria-expanded={colorOpen}
              onClick={() => setColorOpen((o) => !o)}
              style={{
                width: 18, height: 18, borderRadius: 4,
                border: '2px solid var(--gold)', background: drawColor, cursor: 'pointer', padding: 0,
              }}
            />
            {colorOpen ? (
              <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                {(theme === 'ivory'
                  ? ['#2a241c', '#8a6c12', '#187a48', '#b53a3a', '#3b6fd4', '#7c3aed']
                  : ['#e8edf5', '#c9a227', '#3dba7a', '#e25b5b', '#5b8def', '#c084fc']
                ).map((c) => (
                  <button
                    key={c}
                    title={c}
                    onClick={() => { setDrawColor(c); setColorOpen(false); }}
                    style={{
                      width: 18, height: 18, borderRadius: 4,
                      border: drawColor === c ? '2px solid var(--gold)' : '1px solid var(--line)',
                      background: c, cursor: 'pointer', padding: 0,
                    }}
                  />
                ))}
              </span>
            ) : null}
            <button
              type="button"
              style={ghostBtn}
              onClick={undoDraw}
              disabled={histIdx <= 0}
              title={t.undoTitle}
            >{t.undo}</button>
            <button
              type="button"
              style={ghostBtn}
              onClick={redoDraw}
              disabled={histIdx < 0 || histIdx >= histRef.current.length - 1}
              title={t.redoTitle}
            >{t.redo}</button>
            <button type="button" style={ghostBtn} onClick={() => void saveDrawings()} disabled={!drawDirty} title={t.saveChart}>
              {drawDirty ? t.saveChart : t.saved}
            </button>
            <button type="button" style={ghostBtn} onClick={() => void clearRange()} title={t.clearRangeTitle}>{t.clearRange}</button>
            {active?.trade_range ? (
              <span style={{ fontSize: 12, color: 'var(--gold)', fontFamily: 'var(--mono)' }}>
                {fmtRange(active.trade_range, locale)}
              </span>
            ) : null}
          </div>

          <div style={{
            border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden',
            background: termDock === 'terminal' && !chartFocus ? 'var(--panel-2)' : 'var(--chart-bg)',
            // Phone: keep a real chart slab so the cream void between pair/tools doesn't look "empty desk".
            minHeight: termDock === 'chart' || chartFocus
              ? (phoneLayout ? 280 : 0)
              : (termDock === 'terminal' ? 0 : (phoneLayout ? 300 : 360)),
            height: '100%',
            position: 'relative',
          }}>
            {/* Keep ChartPane mounted when basement covers it — only hide visually. */}
            <div style={{
              position: 'absolute', inset: 0,
              visibility: termDock === 'terminal' && !chartFocus ? 'hidden' : 'visible',
              pointerEvents: termDock === 'terminal' && !chartFocus ? 'none' : 'auto',
            }}>
              <ChartPane
                key={active?.id || seriesKey}
                candles={candles}
                seriesKey={seriesKey}
                candlesKey={candlesKey}
                range={active?.trade_range || null}
                payload={payload}
                tool={tool}
                drawColor={drawColor}
                rangeUseTime={rangeUseTime}
                timeframe={active?.timeframe || '15m'}
                strategyName={chipCopy(t, strategyKind).name}
                backtestMarkers={active?.trade_range ? backtestMarkers : []}
                liveMarkers={liveMarkers}
                levelDashes={levelDashes}
                sketchDashes={sketchDashes}
                sketchDashKey={sketchDashKey}
                avgEntryLevel={avgEntryLevel}
                strategyStartTime={
                  activeLiveRun?.created_at
                    ? Math.floor(new Date(String(activeLiveRun.created_at)).getTime() / 1000)
                    : null
                }
                selectedId={selectedDrawId}
                onSelect={setSelectedDrawId}
                onAvgEntryClick={() => {
                  if (!activeOpenPos) return;
                  const key = positionKey(activeOpenPos);
                  selectPosition(selectedPosKey === key ? null : key);
                }}
                onPayloadChange={onPayloadChange}
                onRangePick={(box) => void onRangePick(box)}
                onRangeClear={() => void clearRange()}
                live={status === 'live'}
                onNeedOlderHistory={() => void loadMoreHistory()}
                historyLoading={historyBusy}
                historyExhausted={historyExhausted}
                depth={depth}
              />
            </div>
            {termDock === 'terminal' && !chartFocus ? (
              <div style={{
                position: 'relative', zIndex: 2, height: '100%',
                display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px',
              }}>
                <button
                  type="button"
                  onClick={() => setTermDock('split')}
                  style={{ ...ghostBtn, padding: '5px 10px', fontSize: 12 }}
                  title={t.termExpandChartTitle}
                >
                  {t.termExpandChart}
                </button>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>{t.termChartCovered}</span>
              </div>
            ) : null}
            {selectedOpenPos && chartFocus ? (
              <div style={{
                position: 'absolute', right: 10, bottom: 10, zIndex: 12,
                width: 'min(340px, calc(100% - 20px))',
                boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
              }}>
                {positionToolsNode}
              </div>
            ) : null}
            {termDock === 'split' && !chartFocus ? (
              <button
                type="button"
                onClick={() => setTermDock('chart')}
                title={t.termExpandChartTitle}
                style={{
                  position: 'absolute', right: 10, bottom: 10, zIndex: 9,
                  ...ghostBtn, padding: '5px 10px', fontSize: 12,
                  background: 'var(--overlay-chip)',
                }}
              >
                {t.termExpandChart}
              </button>
            ) : null}
          </div>

          {desktopSplit ? (
            <div
              role="separator"
              aria-orientation="horizontal"
              aria-valuenow={basementH}
              aria-valuemin={BASEMENT_H_MIN}
              aria-valuemax={BASEMENT_H_MAX}
              title="Drag to resize chart / panel · double-click resets"
              onPointerDown={beginBasementResize}
              onDoubleClick={() => setBasementH(BASEMENT_H_DEFAULT)}
              style={{
                height: 8, margin: '0 2px', borderRadius: 6, cursor: 'row-resize',
                background: paneDrag === 'basement'
                  ? 'rgba(201,162,39,0.45)'
                  : 'rgba(201,162,39,0.14)',
                border: '1px solid var(--line)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                touchAction: 'none',
              }}
            >
              <span style={{
                width: 36, height: 3, borderRadius: 2,
                background: 'var(--gold)', opacity: 0.7,
              }} />
            </div>
          ) : null}

          {!chartFocus ? (
            termDock === 'chart' ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, minHeight: 0, height: '100%',
                padding: '0 10px', borderRadius: 10, border: '1px solid var(--line)',
                background: 'var(--panel-2)',
              }}>
                <button
                  type="button"
                  onClick={() => setTermDock('split')}
                  style={{ ...ghostBtn, padding: '5px 10px', fontSize: 12 }}
                  title={t.termExpandPanelTitle}
                >
                  {t.termExpandPanel}
                </button>
                <span style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                  {lastClose != null ? `last ${lastClose.toFixed(2)}` : '—'}
                </span>
              </div>
            ) : (
              <div ref={terminalRef} style={{ minHeight: 0, height: '100%' }}>
                <TerminalPanel
                  tab={termTab}
                  onTab={setTermTab}
                  depth={depth}
                  positions={account?.positions || []}
                  orders={orders}
                  trades={closedTrades}
                  marketTape={trades}
                  balance={account?.balance || null}
                  hasKey={Boolean(activeKeyId)}
                  chipBySymbol={chipBySymbol}
                  lastPrice={lastClose}
                  marginLabel={exchangeMargin
                    ? `${String(exchangeMargin.marginType || '').toUpperCase() === 'ISOLATED' ? 'Isolated' : 'Cross'} · ${
                        String(exchangeMargin.marginType || '').toUpperCase() === 'ISOLATED'
                          ? (exchangeMargin.isolatedLongLeverage || exchangeMargin.isolatedShortLeverage || '—')
                          : (exchangeMargin.crossLeverage || '—')
                      }x`
                    : null}
                  onRefreshDepth={() => void loadDepth()}
                  onRefreshTape={() => void loadTrades()}
                  onRefreshTrades={() => { void loadLiveFills(); setMsg(t.closedTradesHint); }}
                  onRefreshAccount={() => void refreshAccount(activeKeyId, active?.symbol)}
                  selectedPositionKey={selectedPosKey}
                  onSelectPosition={(key) => selectPosition(key)}
                  positionTools={positionToolsNode}
                  activeSymbol={active?.symbol || null}
                  groupByActiveTab={posGroupByTab}
                  onGroupByActiveTab={setPosGroupByTab}
                  onJumpToSymbol={(sym) => void jumpToSymbolTab(sym)}
                  dock={termDock}
                  onDockChange={setTermDock}
                  onBookLevelClick={({ price, side: bookSide }) => {
                    if (!(price > 0)) return;
                    const tradeSide = bookSide === 'ask' ? 'buy' : 'sell';
                    setOrderType('LIMIT');
                    setSide(tradeSide);
                    setLimitPrice(String(price));
                    setRightTab('trade');
                    setChartFocus(false);
                    setMsg(t.bookArmedLimit(tradeSide, String(price)));
                  }}
                />
              </div>
            )
          ) : null}
        </div>

        {!chartFocus && !phoneLayout ? (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={asideWidth}
            aria-valuemin={ASIDE_W_MIN}
            aria-valuemax={ASIDE_W_MAX}
            title="Drag to resize order / AI Chip panel · double-click resets"
            onPointerDown={beginAsideResize}
            onDoubleClick={() => setAsideWidth(ASIDE_W_DEFAULT)}
            style={{
              width: 8, height: '100%', cursor: 'col-resize', touchAction: 'none',
              background: paneDrag === 'aside' ? 'rgba(201,162,39,0.4)' : 'var(--panel-2)',
              borderLeft: '1px solid var(--line)',
              borderRight: '1px solid var(--line)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 25,
            }}
          >
            <span style={{
              width: 3, height: 42, borderRadius: 2,
              background: 'var(--gold)', opacity: paneDrag === 'aside' ? 0.95 : 0.5,
            }} />
          </div>
        ) : null}

        {!chartFocus ? (
        <aside style={{
          borderLeft: phoneLayout ? '1px solid var(--line)' : 'none',
          background: 'var(--panel)',
          display: 'grid',
          gridTemplateRows: phoneLayout ? '44px minmax(360px, auto)' : '44px 1fr',
          minHeight: phoneLayout ? Math.max(420, Math.round(phoneW * 1.15)) : 0,
          height: phoneLayout ? 'auto' : '100%',
          width: phoneAsideW,
          maxWidth: phoneAsideW,
          minWidth: phoneAsideW,
          flexShrink: 0,
          boxSizing: 'border-box',
          // start on phone so a tall Cabinet/Chip column cannot inflate the chart column
          alignSelf: phoneLayout ? 'start' : 'stretch',
          position: 'relative', zIndex: 20,
          pointerEvents: 'auto',
        }}>
          <div style={{ display: 'flex', gap: 4, padding: 6, borderBottom: '1px solid var(--line)', position: 'relative', zIndex: 21 }}>
            {([
              ['trade', t.order],
              ['strategy', t.strategies],
              ['keys', t.cabinet],
            ] as const).map(([id, label]) => {
              const stratLive = id === 'strategy' && Boolean(activeRunStatus && ['armed', 'signals_only', 'in_position'].includes(activeRunStatus));
              const selected = rightTab === id;
              return (
              <button
                key={id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setRightTab(id); }}
                onClick={() => setRightTab(id)}
                style={{
                  ...tabBtn, flex: 1,
                  // Selected = gold only. Live strategy keeps a green dot, not a full green tab
                  // (green tab looked “active” while Cabinet content was open).
                  borderColor: selected ? 'var(--gold)' : 'var(--line)',
                  color: selected ? 'var(--gold)' : 'var(--text)',
                  fontSize: 12, padding: '8px 4px',
                  position: 'relative', zIndex: 22,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                }}
              >
                {stratLive ? (
                  <span style={{
                    width: 6, height: 6, borderRadius: 99, background: 'var(--long)',
                    boxShadow: '0 0 6px var(--long)',
                  }} />
                ) : null}
                {label}
              </button>
              );
            })}
          </div>

          <div style={{ overflow: 'auto', padding: 12, minHeight: 0, position: 'relative', zIndex: 22, pointerEvents: 'auto' }}>
            {rightTab === 'trade' && (
              <>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{t.order.toUpperCase()}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <button onClick={() => setSide('buy')} style={{ ...sideBtn, background: side === 'buy' ? 'var(--long)' : 'var(--panel-2)' }}>Buy</button>
                  <button onClick={() => setSide('sell')} style={{ ...sideBtn, background: side === 'sell' ? 'var(--short)' : 'var(--panel-2)' }}>Sell</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
                  {([
                    ['MARKET', 'Market', 'instant fill'],
                    ['LIMIT', 'Limit', 'resting / IOC'],
                    ['STOP_MARKET', 'Stop mkt', 'trigger → market'],
                    ['STOP', 'Stop', 'trigger → limit'],
                  ] as const).map(([id, label, hint]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setOrderType(id)}
                      style={{
                        ...tabBtn,
                        borderColor: orderType === id ? 'var(--gold)' : 'var(--line)',
                        padding: '8px 6px',
                        display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'stretch',
                      }}
                    >
                      <span>{label}</span>
                      <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>{hint}</span>
                    </button>
                  ))}
                </div>
                {(orderType === 'LIMIT' || orderType === 'STOP') ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 8 }}>
                    {([
                      ['GTC', 'GTC', 'good till cancel'],
                      ['IOC', 'IOC', 'immediate or cancel'],
                      ['FOK', 'FOK', 'fill or kill'],
                    ] as const).map(([id, label, hint]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setTimeInForce(id)}
                        style={{
                          ...tabBtn,
                          borderColor: timeInForce === id ? 'var(--gold)' : 'var(--line)',
                          padding: '6px 4px',
                          display: 'flex', flexDirection: 'column', gap: 2,
                        }}
                      >
                        <span style={{ fontSize: 12 }}>{label}</span>
                        <span style={{ fontSize: 9, color: 'var(--muted)' }}>{hint}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
                <label style={labelStyle}>{t.size}</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 4 }}>
                  {([
                    ['coins', t.sizeModeCoins],
                    ['usdt', t.sizeModeUsdt],
                    ['pct', t.sizeModePct],
                  ] as const).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setSizeMode(id)}
                      style={{
                        ...tabBtn,
                        borderColor: sizeMode === id ? 'var(--gold)' : 'var(--line)',
                        color: sizeMode === id ? 'var(--gold)' : 'var(--text)',
                        padding: '6px 4px',
                        fontSize: 11,
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  style={inputStyle}
                  placeholder={sizeMode === 'pct' ? '2' : sizeMode === 'usdt' ? '50' : '0.01'}
                />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.35 }}>
                  {sizeMode === 'coins' ? t.sizeHintCoins
                    : sizeMode === 'usdt' ? t.sizeHintUsdt
                      : t.sizeHintPct}
                  {(() => {
                    const q = resolveOrderQty();
                    if (!(q > 0) || !Number.isFinite(q)) return null;
                    const px = entryForProtective || lastClose || 0;
                    const noto = px > 0 ? q * px : NaN;
                    return (
                      <span style={{ color: 'var(--gold)', marginLeft: 6 }}>
                        ≈ {Number(q.toPrecision(5))} · {Number.isFinite(noto) ? `${noto.toFixed(2)} USDT` : ''}
                      </span>
                    );
                  })()}
                </div>
                {(orderType === 'LIMIT' || orderType === 'STOP') ? (
                  <>
                    <label style={labelStyle}>{t.price}</label>
                    <input value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)} placeholder={String(lastClose ?? '')} style={inputStyle} />
                  </>
                ) : null}
                {(orderType === 'STOP' || orderType === 'STOP_MARKET') ? (
                  <>
                    <label style={labelStyle}>{t.stopPrice}</label>
                    <input value={stopPrice} onChange={(e) => setStopPrice(e.target.value)} placeholder={String(lastClose ?? '')} style={inputStyle} />
                  </>
                ) : null}
                <ProtectiveOrders
                  side={side}
                  entryPrice={entryForProtective}
                  qty={String(resolveOrderQty() || qty)}
                  value={protective}
                  onChange={setProtective}
                  labels={{
                    title: t.protectiveTitle,
                    hint: t.protectiveHint,
                    tp: t.protectiveTp,
                    sl: t.protectiveSl,
                    trail: t.protectiveTrail,
                    off: t.protectiveOff,
                    pct: t.protectivePct,
                    price: t.protectivePrice,
                    callback: t.protectiveCallback,
                    activate: t.protectiveActivate,
                    preview: t.protectivePreview,
                    tpPnl: t.protectiveTpPnl,
                    slPnl: t.protectiveSlPnl,
                  }}
                />
                {activeMarket === 'futures' ? (
                  <MarginControls
                    mode={marginMode}
                    leverage={leverage}
                    exchange={exchangeMargin}
                    busy={marginBusy}
                    applyDisabled={!activeKeyId}
                    metrics={marginMetrics}
                    onMode={setMarginMode}
                    onLeverage={setLeverage}
                    onApply={() => void applyMargin()}
                    labels={{
                      title: t.margin,
                      exchange: t.marginExchange,
                      apply: t.marginApply,
                      cross: t.cross,
                      isolated: t.isolated,
                      leverage: t.leverage,
                      available: t.marginAvailable,
                      used: t.marginUsed,
                      maintenance: t.marginMaint,
                      equity: t.marginEquity,
                      effectiveLev: t.marginEffLev,
                      ratio: t.marginRatio,
                    }}
                  />
                ) : (
                  <div style={{
                    marginTop: 8, padding: '8px 10px', borderRadius: 8,
                    border: '1px solid var(--line)', background: 'var(--panel-2)',
                    fontSize: 12, color: 'var(--muted)', lineHeight: 1.4,
                  }}>
                    Spot · long only · size from deposit (≤98% so exits still fit)
                  </div>
                )}
                <button
                  style={{ ...primaryBtn, marginTop: 10, opacity: activeKeyId && !orderBusy ? 1 : 0.7 }}
                  disabled={!activeKeyId || orderBusy}
                  onClick={() => void placeOrder()}
                >
                  {activeKeyId ? `${orderType} ${side}` : t.needKey}
                </button>
              </>
            )}

            {rightTab === 'strategy' && (
              <StrategyPanel
                kind={strategyKind}
                params={strategyParams}
                onlyProfitPct={onlyProfitPct}
                onlyProfitEnabled={onlyProfitEnabled}
                riskPct={riskPct}
                riskMaxPct={riskMaxPct}
                running={Boolean(activeRunStatus && ['armed', 'signals_only', 'in_position'].includes(activeRunStatus))}
                runStatus={activeRunStatus}
                runKindLabel={
                  activeLiveRun?.kind && activeLiveRun.kind in STRATEGY_CATALOG
                    ? chipCopy(t, activeLiveRun.kind as StrategyKind).name
                    : null
                }
                runSymbol={activeLiveRun?.symbol || active?.symbol || null}
                hasKey={Boolean(activeKeyId)}
                strategyLabel={chipCopy(t, strategyKind).name}
                onKind={setKind}
                onParam={setStrategyParam}
                onOnlyProfitPct={(v) => {
                  setOnlyProfitPct(v);
                  queueRiskPersist(riskPct, riskMaxPct, v, onlyProfitEnabled);
                }}
                onOnlyProfitEnabled={(v) => {
                  setOnlyProfitEnabled(v);
                  queueRiskPersist(riskPct, riskMaxPct, onlyProfitPct, v);
                }}
                onRiskPct={(v) => {
                  setRiskPct(v);
                  queueRiskPersist(v, riskMaxPct, onlyProfitPct, onlyProfitEnabled);
                }}
                onRiskMaxPct={(v) => {
                  setRiskMaxPct(v);
                  queueRiskPersist(riskPct, v, onlyProfitPct, onlyProfitEnabled);
                }}
                onArm={() => void armStrategy()}
                onStop={() => setStopDialog(true)}
                rangeLabel={fmtRange(active?.trade_range, locale) || undefined}
                rangeTradesCount={active?.trade_range ? backtestMarkers.length : 0}
                bookHint={bookHint}
                spotMode={activeMarket === 'spot'}
                marginMode={marginMode}
                leverage={leverage}
                exchangeMargin={exchangeMargin}
                marginBusy={marginBusy}
                marginMetrics={marginMetrics}
                onMarginMode={setMarginMode}
                onLeverage={setLeverage}
                onApplyMargin={() => void applyMargin()}
                sizeWarn={sizeWarn}
                stopMode={
                  activeLiveRun?.params?.stopAfterExit
                    ? 'soft'
                    : lastStopMode
                }
                runNotice={
                  activeLiveRun?.params?.stopAfterExit
                    ? t.strategySoftActive
                    : runNotice
                }
              />
            )}

            {rightTab === 'keys' && (
              <>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{t.personalCabinet}</div>
                <div style={{
                  padding: 10, borderRadius: 10, border: '1px solid var(--line)', background: 'var(--panel-2)', marginBottom: 10,
                }}>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t.account}</div>
                  <div style={{ marginTop: 6, fontFamily: 'var(--mono)', fontSize: 13 }}>{me?.email || '—'}</div>
                  <div style={{ marginTop: 4, fontSize: 12, color: 'var(--muted)' }}>
                    {t.plan} {plan?.name || me?.planId || 'free'} · {plan?.tabLimit || 2} {t.tabs}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>{t.depositWallet}</div>
                  <div style={{
                    marginTop: 4, fontSize: 11, fontFamily: 'var(--mono)', wordBreak: 'break-all',
                    color: 'var(--gold)', lineHeight: 1.4,
                  }} title={t.depositWalletHint}>
                    {depositWallet?.address || '…'}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: 'var(--muted)' }}>
                    Aptos USDT · bal {depositWallet ? depositWallet.balanceUsdt.toFixed(2) : '—'}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>{t.changePassword}</div>
                  <input
                    type="password"
                    placeholder={t.currentPassword}
                    value={pwdForm.current}
                    onChange={(e) => setPwdForm((p) => ({ ...p, current: e.target.value }))}
                    style={{ ...inputStyle, marginTop: 4 }}
                  />
                  <input
                    type="password"
                    placeholder={t.newPassword}
                    value={pwdForm.next}
                    onChange={(e) => setPwdForm((p) => ({ ...p, next: e.target.value }))}
                    style={{ ...inputStyle, marginTop: 6 }}
                  />
                  <button type="button" style={{ ...ghostBtn, width: '100%', marginTop: 8 }} onClick={() => void changePwd()}>
                    {t.savePassword}
                  </button>
                  <div style={{ marginTop: 14, fontSize: 12, color: 'var(--muted)' }}>{t.tgTitle}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, marginTop: 4 }}>{t.tgHint}</div>
                  <input
                    value={tgHandle}
                    onChange={(e) => setTgHandle(e.target.value)}
                    placeholder={t.tgPlaceholder}
                    style={{ ...inputStyle, marginTop: 6 }}
                    autoComplete="off"
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
                    <button
                      type="button"
                      style={ghostBtn}
                      onClick={() => {
                        const raw = tgHandle.trim().replace(/^@+/, '');
                        if (!raw) { setMsg(t.tgNeedHandle); return; }
                        const normalized = `@${raw}`;
                        setTgHandle(normalized);
                        try { localStorage.setItem(TG_HANDLE_KEY, normalized); } catch { /* ignore */ }
                        setMsg(t.tgSaved(normalized));
                      }}
                    >
                      {t.tgSave}
                    </button>
                    <a
                      href={(() => {
                        const raw = tgHandle.trim().replace(/^@+/, '');
                        if (!raw || !TG_BOT) return undefined;
                        const start = encodeURIComponent(`u_${me?.id || 'guest'}__${raw}`);
                        return `https://t.me/${TG_BOT}?start=${start}`;
                      })()}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => {
                        const raw = tgHandle.trim().replace(/^@+/, '');
                        if (!raw) { e.preventDefault(); setMsg(t.tgNeedHandle); return; }
                        if (!TG_BOT) { e.preventDefault(); setMsg(t.tgBotMissing); }
                      }}
                      style={{
                        ...ghostBtn, textDecoration: 'none', textAlign: 'center',
                        borderColor: 'var(--gold)', color: 'var(--gold)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {t.tgOpenBot}
                    </a>
                  </div>
                </div>
                <div
                  ref={cabinetCardRef}
                  style={{
                    padding: 10, borderRadius: 10,
                    border: `1px solid ${cabinetExpanded ? 'var(--gold)' : 'var(--line)'}`,
                    background: 'var(--panel-2)', marginBottom: 10,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                    <div style={{ fontSize: 12, color: 'var(--text)', opacity: 0.78 }}>{t.tabLabel} · {active?.symbol || '—'}</div>
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 4,
                      border: `1px solid ${
                        accountHealth === 'ok' ? 'var(--long)'
                          : accountHealth === 'error' || accountHealth === 'empty' ? 'var(--gold)'
                            : activeKeyId ? 'var(--line)' : 'var(--line)'
                      }`,
                      color: accountHealth === 'ok' ? 'var(--long)'
                        : accountHealth === 'error' ? 'var(--short)'
                          : accountHealth === 'empty' ? 'var(--gold)'
                            : activeKeyId ? 'var(--muted)' : 'var(--muted)',
                    }}>
                      {!activeKeyId ? t.noKey
                        : accountHealth === 'ok' ? t.accountOk
                          : accountHealth === 'empty' ? t.accountEmpty
                            : accountHealth === 'error' ? t.accountFail
                              : t.keyOk}
                    </span>
                  </div>
                  <div style={{ marginTop: 8, fontFamily: 'var(--mono)', fontSize: 13, lineHeight: 1.55 }}>
                    <div style={{ color: 'var(--muted)', fontSize: 11, marginBottom: 4 }}>
                      {activeMarket === 'spot' ? t.monitorMarketSpot : t.monitorMarketFutures}
                      {' · '}
                      {t.monitorEquitySeparate}
                    </div>
                    <div>{t.marginEquity}: <b style={{ color: 'var(--gold)' }}>{account?.balance ? fmtPrice(account.balance.equity, 2) : '—'}</b> USDT</div>
                    <div style={{ color: 'var(--muted)' }}>
                      {activeMarket === 'spot' ? t.monitorSpotAvailable : t.marginAvailable}: {marginMetrics?.availableMargin != null
                        ? fmtPrice(marginMetrics.availableMargin, 2)
                        : account?.balance
                          ? fmtPrice(account.balance.availableMargin ?? account.balance.available, 2)
                          : '—'} USDT
                    </div>
                    {siblingBalance ? (
                      <div style={{ color: 'var(--muted)', marginTop: 2 }}>
                        {siblingBalance.market === 'spot' ? t.monitorMarketSpot : t.monitorMarketFutures}
                        {' '}{t.marginEquity}: {fmtPrice(siblingBalance.equity, 2)}
                        {' · '}
                        {fmtPrice(siblingBalance.available, 2)} {t.monitorAvailShort}
                      </div>
                    ) : null}
                    <div style={{ color: 'var(--muted)' }}>
                      {t.marginUsed}: {marginMetrics?.usedMargin != null
                        ? fmtPrice(marginMetrics.usedMargin, 2)
                        : '—'} USDT
                    </div>
                    <div style={{ color: 'var(--muted)' }}>
                      {t.marginMaint}: {marginMetrics?.maintenanceMargin != null
                        ? fmtPrice(marginMetrics.maintenanceMargin, 2)
                        : '—'} USDT
                    </div>
                    <div style={{ color: 'var(--gold)' }}>
                      {t.marginEffLev}: {marginMetrics?.effectiveLeverage != null
                        ? `${marginMetrics.effectiveLeverage.toFixed(2)}x`
                        : '—'}
                    </div>
                    <div style={{ marginTop: 2, color: uPnL >= 0 ? 'var(--long)' : 'var(--short)' }}>
                      uPnL: {fmtPrice(uPnL, 2)} USDT
                    </div>
                    <div style={{ color: 'var(--muted)' }}>
                      {t.positionsCount}: {account?.positions?.length ?? 0} · {t.plan} {plan?.name || '—'}
                      {plan?.priceUsdtMonthly ? ` · $${plan.priceUsdtMonthly}${t.perMonth}` : ` · ${t.freePlan}`}
                    </div>
                    {!activeKeyId ? (
                      <div style={{ marginTop: 6, color: 'var(--short)', fontSize: 12 }}>{t.needKeyCabinet}</div>
                    ) : !account?.balance ? (
                      <div style={{ marginTop: 6, color: 'var(--gold)', fontSize: 12 }}>{t.accountRefreshHint}</div>
                    ) : null}
                  </div>
                  <div style={{ marginTop: 8 }} title={t.monitorChartsHint}>
                    <div style={{
                      display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center',
                    }}>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{t.monitorChartFor}</span>
                      {([
                        ['futures', t.monitorMarketFutures],
                        ['spot', t.monitorMarketSpot],
                      ] as const).map(([id, label]) => {
                        const on = monitorMarket === id;
                        return (
                          <button
                            key={id}
                            type="button"
                            title={t.monitorSwitchMarketHint}
                            onClick={() => setMonitorMarket(id)}
                            style={{
                              padding: '3px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                              border: `1px solid ${on ? 'var(--gold)' : 'var(--line)'}`,
                              color: on ? 'var(--gold)' : 'var(--text)',
                              background: on ? 'rgba(201,162,39,0.12)' : 'var(--panel)',
                              fontWeight: on ? 700 : 500,
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <MonitorSparklines
                      samples={equitySamples}
                      tall
                      labels={{
                        equity: monitorMarket === 'spot' ? t.monitorSpotBalance : t.monitorEquityChart,
                        pnl: t.monitorPnlChart,
                        empty: t.monitorChartsEmpty,
                        deposit: t.monitorDeposit,
                        period15m: t.monitorPeriod15m,
                        period1h: t.monitorPeriod1h,
                        period6h: t.monitorPeriod6h,
                        period24h: t.monitorPeriod24h,
                        periodAll: t.monitorPeriodAll,
                      }}
                    />
                    <div style={{
                      marginTop: 4, fontSize: 10, color: 'var(--muted)', fontFamily: 'var(--mono)', textAlign: 'center',
                    }}>
                      {activeKeyId
                        ? t.monitorMeta(equitySamples.length, t.monitorPollEvery)
                        : t.noKey}
                    </div>
                  </div>
                  {cabinetExpanded ? (
                    <div style={{
                      marginTop: 8, padding: 8, borderRadius: 8, border: '1px solid var(--line)',
                      background: 'var(--bg)', fontSize: 11, fontFamily: 'var(--mono)', lineHeight: 1.5,
                    }}>
                      <div style={{ color: 'var(--muted)', marginBottom: 4 }}>{t.positions}</div>
                      {(account?.positions || []).length === 0 ? (
                        <div style={{ color: 'var(--muted)' }}>—</div>
                      ) : (account?.positions || []).slice(0, 6).map((p: any, i: number) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span>{p.symbol || '—'}</span>
                          <span style={{ color: Number(p.unrealizedPnl) >= 0 ? 'var(--long)' : 'var(--short)' }}>
                            {fmtPrice(p.unrealizedPnl, 2)}
                          </span>
                        </div>
                      ))}
                      <div style={{ color: 'var(--muted)', marginTop: 8 }}>{t.orders}: {orders.length}</div>
                    </div>
                  ) : null}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 10, position: 'relative', zIndex: 12 }}>
                    <button
                      type="button"
                      disabled={monitorBusy}
                      style={{
                        ...ghostBtn, opacity: activeKeyId ? 1 : 0.85, cursor: 'pointer',
                        borderColor: 'var(--gold)', color: 'var(--gold)',
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Open monitor view (not a silent refresh): expand card + Balance tab
                        setRightTab('keys');
      setChartFocus(false);
                        setCabinetExpanded(true);
                        setTermTab('balance');
                        if (!activeKeyId) {
                          setMsg(t.needKeyCabinet);
                          return;
                        }
                        setMonitorBusy(true);
                        void (async () => {
                          try {
                            await refreshAccount(activeKeyId, active?.symbol);
                            await refreshMargin(activeKeyId, active?.symbol);
                            setMsg(t.monitoringOpened);
                          } finally {
                            setMonitorBusy(false);
                            requestAnimationFrame(() => {
                              cabinetCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                              terminalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            });
                          }
                        })();
                      }}
                      title={t.accountRefreshHint}
                    >
                      {monitorBusy ? t.monitoringBusy : `↻ ${t.monitoring}`}
                    </button>
                    <button
                      type="button"
                      style={{ ...ghostBtn, cursor: 'pointer' }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const next = !cabinetExpanded;
                        setCabinetExpanded(next);
                        setRightTab('keys');
                        setChartFocus(false);
                        if (next) setTermTab('balance');
                        if (!activeKeyId) setMsg(t.needKeyCabinet);
                        else void refreshAccount(activeKeyId, active?.symbol);
                        requestAnimationFrame(() => {
                          if (next) terminalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        });
                      }}
                    >
                      {cabinetExpanded ? t.expandCollapse : t.expand}
                    </button>
                  </div>
                </div>

                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>{t.apiKeysOne}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8, lineHeight: 1.4 }}>
                  {t.keyShareHint}
                </div>
                {keys.length > 0 ? (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>{t.keySavedList}</div>
                    <div style={{ display: 'grid', gap: 6, marginBottom: 8 }}>
                      {keys.map((k) => {
                        const isActive = activeKeyId === k.id;
                        return (
                          <div
                            key={k.id}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              gap: 8,
                              alignItems: 'center',
                              padding: '8px 10px',
                              borderRadius: 8,
                              border: `1px solid ${isActive ? 'var(--gold)' : 'var(--line)'}`,
                              background: 'var(--panel-2)',
                            }}
                          >
                            <button
                              type="button"
                              disabled={keyBusy}
                              onClick={() => {
                                setSelectedKeyId(k.id);
                                void patchActive({ apiKeyId: k.id });
                                void refreshAccount(k.id, active?.symbol);
                              }}
                              style={{
                                background: 'transparent', border: 'none', padding: 0, textAlign: 'left',
                                cursor: 'pointer', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12,
                              }}
                            >
                              <div style={{ color: isActive ? 'var(--gold)' : 'var(--text)' }}>
                                {k.alias}{isActive ? ' · tab' : ''}
                              </div>
                              <div style={{ color: 'var(--muted)', fontSize: 10 }}>
                                {k.status} · {k.id.slice(0, 8)}…
                              </div>
                            </button>
                            <button
                              type="button"
                              disabled={keyBusy}
                              onClick={() => void deleteKey(k)}
                              style={{
                                ...ghostBtn,
                                padding: '6px 10px',
                                borderColor: 'var(--short)',
                                color: 'var(--short)',
                                opacity: keyBusy ? 0.6 : 1,
                              }}
                            >
                              {t.deleteKey}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <select
                      value={activeKeyId}
                      onChange={(e) => {
                        setSelectedKeyId(e.target.value);
                        void patchActive({ apiKeyId: e.target.value || null });
                      }}
                      style={selectStyle}
                    >
                      <option value="">{t.noKeyOption}</option>
                      {keys.map((k) => <option key={k.id} value={k.id}>{k.alias} · {k.status}</option>)}
                    </select>
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t.keyNotConnected}</div>
                )}
                {liveRuns.filter((r) => !activeKeyId || !r.api_key_id || r.api_key_id === activeKeyId).length > 0 ? (
                  <div style={{
                    marginTop: 10, padding: 8, borderRadius: 8,
                    border: '1px solid rgba(61,186,122,0.35)', background: 'rgba(61,186,122,0.06)',
                  }}>
                    <div style={{ fontSize: 11, color: 'var(--long)', marginBottom: 4 }}>{t.liveBotsOther}</div>
                    {liveRuns
                      .filter((r) => !activeKeyId || !r.api_key_id || r.api_key_id === activeKeyId)
                      .map((r) => (
                        <div key={r.id} style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text)', marginTop: 2 }}>
                          ● {r.symbol} · {r.kind} · {r.status}
                        </div>
                      ))}
                  </div>
                ) : null}
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', lineHeight: 1.4 }}>
                  {t.keyReplaceHint}
                </div>
                {!showKeyForm ? (
                  <button
                    type="button"
                    disabled={keyBusy}
                    onClick={() => setShowKeyForm(true)}
                    style={{ ...ghostBtn, width: '100%', marginTop: 8, opacity: keyBusy ? 0.6 : 1 }}
                  >
                    {keys.length ? t.addKey : t.connectKey}
                  </button>
                ) : (
                  <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                    <input placeholder="alias" value={keyForm.alias} onChange={(e) => setKeyForm({ ...keyForm, alias: e.target.value })} style={inputStyle} />
                    <input placeholder="API Key" value={keyForm.apiKey} onChange={(e) => setKeyForm({ ...keyForm, apiKey: e.target.value })} style={inputStyle} autoComplete="off" />
                    <input placeholder="API Secret" type="password" value={keyForm.apiSecret} onChange={(e) => setKeyForm({ ...keyForm, apiSecret: e.target.value })} style={inputStyle} autoComplete="off" />
                    <input placeholder="Passphrase" type="password" value={keyForm.passphrase} onChange={(e) => setKeyForm({ ...keyForm, passphrase: e.target.value })} style={inputStyle} autoComplete="off" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <button type="button" disabled={keyBusy} onClick={() => setShowKeyForm(false)} style={ghostBtn}>{t.cancel}</button>
                      <button
                        type="button"
                        disabled={keyBusy}
                        onClick={() => void saveKey()}
                        style={{ ...primaryBtn, opacity: keyBusy ? 0.7 : 1 }}
                      >
                        {keyBusy ? t.keySaving : t.save}
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 12, fontSize: 11, color: 'var(--muted)', lineHeight: 1.45 }}>
                  {t.billingHint}
                </div>
              </>
            )}
          </div>
        </aside>
        ) : (
          <button
            type="button"
            title={t.showPanels}
            onClick={() => setChartFocus(false)}
            style={{
              position: 'absolute', right: 8, top: 96, zIndex: 9, ...ghostBtn,
              borderColor: 'var(--gold)', color: 'var(--gold)',
            }}
          >☰</button>
        )}
      </div>
    </div>
  );
}

const tabBtn: CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--panel-2)', color: 'var(--text)', cursor: 'pointer' };
const ghostBtn: CSSProperties = { ...tabBtn, background: 'transparent' };
const dangerBtn: CSSProperties = { ...tabBtn, borderColor: 'var(--danger)', color: '#fff', background: 'var(--danger)' };
const sideBtn: CSSProperties = { padding: 10, borderRadius: 8, border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer' };
const primaryBtn: CSSProperties = { width: '100%', padding: 12, borderRadius: 10, border: 'none', background: 'linear-gradient(180deg,#dfb83a,var(--gold))', color: '#111', fontWeight: 700, cursor: 'pointer' };
const inputStyle: CSSProperties = { width: '100%', marginTop: 4, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--panel-2)', color: 'var(--text)' };
const selectStyle: CSSProperties = { ...inputStyle };
const compactInput: CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--panel-2)', color: 'var(--text)' };
const compactSelect: CSSProperties = { ...compactInput };
const labelStyle: CSSProperties = { display: 'block', marginTop: 10, fontSize: 12, color: 'var(--muted)' };
